/**
 * Dashboard API client — calls API Gateway `/api/v1/dashboard/*` (proxied in dev & Docker).
 */

import { getTenantExternalId } from "./tenant-settings.js";
import { getAdminJwt } from "./auth-storage.js";
import type { DashboardAuditFilters } from "./audit-filters.js";
import { governanceHeadersJson } from "./governance-http-headers.js";
import type { ConfidenceAction } from "../types";
import type { ScimMappingConfig } from "./scim-mapping.js";

export type { DashboardAuditFilters } from "./audit-filters.js";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ??
  "/api/v1";

export function apiBaseUrl(): string {
  return API_BASE;
}

/**
 * Absolute URL for dashboard SSE (`EventSource` cannot send `Authorization`).
 * Returns null when JWT or tenant is missing.
 */
export function dashboardEventsStreamUrl(): string | null {
  const jwt = getAdminJwt();
  const tenant = getTenantExternalId()?.trim();
  if (!jwt || !tenant) return null;
  const q = new URLSearchParams({
    tenantExternalId: tenant,
    token: jwt,
  });
  const path = `${API_BASE.replace(/\/$/, "")}/dashboard/events-stream?${q.toString()}`;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  if (typeof window === "undefined") return path;
  return new URL(path.startsWith("/") ? path : `/${path}`, window.location.origin).toString();
}

/** Gateway OpenAPI document is mounted under `/api/v1/openapi.json`. */
export function gatewayOpenApiJsonUrl(): string {
  const b = API_BASE.replace(/\/$/, "");
  if (b.startsWith("http")) {
    return b.endsWith("/api/v1") ? `${b}/openapi.json` : `${b}/api/v1/openapi.json`;
  }
  return `${b || "/api/v1"}/openapi.json`;
}

function dashboardHeaders(): HeadersInit {
  const jwt = getAdminJwt();
  if (jwt) {
    return {
      Authorization: `Bearer ${jwt}`,
      /** Required for JWT mutations in production (SEC-07); harmless on GET. */
      "X-IA-Dashboard": "1",
    };
  }
  return {};
}

/** Sprint 5A — active identities per persisted risk band (`unknown` = no row yet). */
export interface IdentityRiskDistribution {
  low: number;
  medium: number;
  high: number;
  critical: number;
  unknown: number;
  /** Active identities assigned a provisional band at read time (no persisted score). */
  provisional?: number;
}

/** Optional block when `DATABASE_URL` is set (persisted audit + elevations). */
export interface PilotKpisPayload {
  auditEvents24h: number;
  revocations24h: number;
  elevationsApproved24h: number;
  elevationsPending: number;
}

export interface DashboardMetricsPayload {
  activeIdentities: number;
  activeSessions: number;
  activeTokens: number;
  totalEvaluations: number;
  decisions: { allow: number; challenge: number; revoke: number };
  /** Epic C (C1) — % of 24h verdicts resolved autonomously vs human review. */
  autonomy?: { autonomous: number; humanReview: number; pctAutonomous: number };
  identityRiskDistribution: IdentityRiskDistribution;
  /** Active identities with no persisted risk row (before provisional assignment). Drives the "Run risk assessment" CTA. */
  unscoredCount?: number;
  /** Present when Postgres metrics are available. */
  pilotKpis?: PilotKpisPayload | null;
  verdiktaStatus: string;
  engineSessionCount: number;
  timestamp: string;
}

/** Public gateway operational JSON (`GET /health/metrics`). */
export interface GatewayHealthMetricsPayload {
  uptime: number;
  activeSessions: number;
  activeTokens: number;
  verdiktaMl: {
    reachable: boolean | null;
    /** Discriminant so the UI distinguishes idle-no-traffic from actually-down. */
    status?: "disabled" | "unknown_no_traffic" | "healthy" | "down" | "circuit_open";
    enabled: boolean;
    urlConfigured: boolean;
    apiKeyConfigured: boolean;
    circuitBreakerEnabled: boolean;
    circuitOpen: boolean;
    lastLatencyMs: number | null;
    p50LatencyMs: number | null;
    lastSuccessAt: string | null;
    lastFailureAt: string | null;
    lastError: string | null;
    recentSampleCount: number;
  };
  timestamp: string;
}

export async function fetchGatewayHealthMetrics(): Promise<GatewayHealthMetricsPayload> {
  const res = await fetch(`${API_BASE}/health/metrics`);
  if (!res.ok) {
    throw new Error(`health metrics failed: ${res.status}`);
  }
  return res.json() as Promise<GatewayHealthMetricsPayload>;
}

const EMPTY_RISK_DISTRIBUTION: IdentityRiskDistribution = {
  low: 0,
  medium: 0,
  high: 0,
  critical: 0,
  unknown: 0,
};

export async function fetchDashboardMetrics(): Promise<DashboardMetricsPayload> {
  const tenantExternalId = getTenantExternalId();
  const q = new URLSearchParams();
  if (tenantExternalId) q.set("tenantExternalId", tenantExternalId);
  const res = await fetch(`${API_BASE}/dashboard/metrics?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) {
    throw new Error(`metrics failed: ${res.status}`);
  }
  const raw = (await res.json()) as Partial<DashboardMetricsPayload> & Record<string, unknown>;
  return {
    ...(raw as DashboardMetricsPayload),
    identityRiskDistribution: {
      ...EMPTY_RISK_DISTRIBUTION,
      ...(typeof raw.identityRiskDistribution === "object" && raw.identityRiskDistribution !== null
        ? (raw.identityRiskDistribution as IdentityRiskDistribution)
        : {}),
    },
  };
}

export async function fetchDashboardAudit(
  limit = 50,
  offset = 0,
  tenantExternalId?: string,
  filters?: DashboardAuditFilters
): Promise<{
  items: unknown[];
  total: number;
  hasMore: boolean;
  limit: number;
  offset: number;
  source?: string;
}> {
  const q = new URLSearchParams();
  q.set("limit", String(limit));
  q.set("offset", String(offset));
  const tid = tenantExternalId?.trim() || getTenantExternalId()?.trim();
  if (tid) q.set("tenantExternalId", tid);
  if (filters?.event) q.set("event", filters.event);
  if (filters?.decision) q.set("decision", filters.decision);
  if (filters?.from) q.set("from", filters.from);
  if (filters?.to) q.set("to", filters.to);
  if (filters?.ip) q.set("ip", filters.ip);
  if (filters?.search) q.set("search", filters.search);
  if (filters?.excludeHttp) q.set("excludeHttp", "true");
  if (filters?.minConfidence != null) q.set("minConfidence", String(filters.minConfidence));
  if (filters?.maxConfidence != null) q.set("maxConfidence", String(filters.maxConfidence));
  const res = await fetch(
    `${API_BASE}/dashboard/audit?${q.toString()}`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    throw new Error(`audit failed: ${res.status}`);
  }
  const data = (await res.json()) as {
    items?: unknown[];
    total?: number;
    hasMore?: boolean;
    limit?: number;
    offset?: number;
    source?: string;
  };
  return {
    items: data.items ?? [],
    total: data.total ?? 0,
    hasMore: data.hasMore ?? false,
    limit: data.limit ?? limit,
    offset: data.offset ?? offset,
    source: data.source,
  };
}

/** A single auth-time denial / step-up row from `/dashboard/access-denials`. */
export type AccessDenialRow = {
  id: number;
  timestamp: string;
  event: "session_access_denied" | "session_step_up_required";
  eventLabel?: string;
  summary?: string;
  sessionId?: string | null;
  ipAddress?: string | null;
  confidenceScore?: number | null;
  identityId?: string | null;
  identityDisplayName?: string | null;
  identityIdentifier?: string | null;
  details?: Record<string, unknown> | null;
  detailsText?: string;
  source?: string;
};

export async function fetchAccessDenials(input?: {
  limit?: number;
  offset?: number;
  cursor?: string;
  reasonCode?: string;
  identityExternalId?: string;
  from?: string;
  to?: string;
  search?: string;
  tenantExternalId?: string;
}): Promise<{
  items: AccessDenialRow[];
  total: number;
  hasMore: boolean;
  limit: number;
  offset: number;
  nextCursor: string | null;
  source?: string;
}> {
  const q = new URLSearchParams();
  const limit = input?.limit ?? 50;
  const offset = input?.offset ?? 0;
  q.set("limit", String(limit));
  q.set("offset", String(offset));
  const tid = input?.tenantExternalId?.trim() || getTenantExternalId()?.trim();
  if (tid) q.set("tenantExternalId", tid);
  if (input?.cursor) q.set("cursor", input.cursor);
  if (input?.reasonCode) q.set("reasonCode", input.reasonCode);
  if (input?.identityExternalId) q.set("identityExternalId", input.identityExternalId);
  if (input?.from) q.set("from", input.from);
  if (input?.to) q.set("to", input.to);
  if (input?.search) q.set("search", input.search);
  const res = await fetch(
    `${API_BASE}/dashboard/access-denials?${q.toString()}`,
    { headers: dashboardHeaders() },
  );
  if (!res.ok) {
    throw new Error(`access-denials failed: ${res.status}`);
  }
  const data = (await res.json()) as {
    items?: AccessDenialRow[];
    total?: number;
    hasMore?: boolean;
    limit?: number;
    offset?: number;
    nextCursor?: string | null;
    source?: string;
  };
  return {
    items: data.items ?? [],
    total: data.total ?? 0,
    hasMore: data.hasMore ?? false,
    limit: data.limit ?? limit,
    offset: data.offset ?? offset,
    nextCursor: data.nextCursor ?? null,
    source: data.source,
  };
}

/**
 * CSV export via dashboard audit route (same auth as live audit). Uses server-side filters.
 */
export async function exportDashboardAuditCsv(input: {
  tenantExternalId: string;
  filters?: DashboardAuditFilters;
}): Promise<void> {
  const q = new URLSearchParams();
  q.set("format", "csv");
  q.set("tenantExternalId", input.tenantExternalId);
  q.set("limit", "10000");
  if (input.filters?.event) q.set("event", input.filters.event);
  if (input.filters?.decision) q.set("decision", input.filters.decision);
  if (input.filters?.from) q.set("from", input.filters.from);
  if (input.filters?.to) q.set("to", input.filters.to);
  if (input.filters?.ip) q.set("ip", input.filters.ip);
  if (input.filters?.search) q.set("search", input.filters.search);
  if (input.filters?.excludeHttp) q.set("excludeHttp", "true");
  if (input.filters?.minConfidence != null) {
    q.set("minConfidence", String(input.filters.minConfidence));
  }
  if (input.filters?.maxConfidence != null) {
    q.set("maxConfidence", String(input.filters.maxConfidence));
  }
  const res = await fetch(`${API_BASE}/dashboard/audit?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) {
    throw new Error(`audit export failed: ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-${input.tenantExternalId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface DashboardSessionRow {
  sessionId: string;
  identityId: string;
  tenantId: string;
  confidenceScore: number | null;
  action: "allow" | "challenge" | "downgrade" | "revoke";
  lastAccessedAt: string;
  /** Real session start, from the sessions row. */
  startedAt?: string | null;
  /** Real session expiry; null for context-only sessions. */
  expiresAt?: string | null;
  /** Bound device id when the session row carries one. */
  deviceId?: string | null;
  /** Last continuous-eval timestamp. */
  lastEvaluatedAt?: string | null;
  vectorScores: Record<string, number> | null;
  /** From session context store geo signal when available */
  ipAddress?: string | null;
  /**
   * RFC-004 R3.3 — persisted, observed-only country (null when the value
   * would have been a geo default). Present on active and history rows.
   */
  country?: string | null;
  /** RFC-004 R3.3 — OAuth/OIDC app client id persisted on the session row. */
  appClientId?: string | null;
  /** Renewal-gated enforcement tier (Sprints 1–8). */
  enforcementTier?: number | null;
  /** Last successful renewal, ms epoch (renewal-cadence hint). */
  lastRenewalAt?: number | null;
  /** OP session state machine value (active/challenged/downgraded/revoked/expired). */
  opStatus?: string | null;
  /** From identity governance when the identity row exists */
  identityType?: string | null;
  /** Human-friendly identity name (display name) resolved server-side. */
  identityDisplayName?: string | null;
  /** Identity login identifier (email / UPN) resolved server-side. */
  identityIdentifier?: string | null;
  /** Multi-dimensional scoring: categorical risk status. */
  riskStatus: "low" | "medium" | "high" | "critical";
  /** Multi-dimensional scoring: dedicated anomaly score (0-100). */
  anomalyScore: number | null;
  /** Multi-dimensional scoring: dedicated behavioral score (0-100). */
  behavioralScore: number | null;
  /** Human-readable reasoning from the Verdikta engine. */
  reasoning: string | null;
  /** Per-vector weights and signals from the latest evaluation. */
  vectorEvaluations?: Array<{
    vector: string;
    score: number;
    weight: number;
    signals: string[];
  }> | null;
  contributions?: Array<{
    vector: string;
    marginalContribution: number;
    gateBinding?: boolean;
  }> | null;
  evidenceQuality?: number | null;
  riskGateApplied?: boolean;
  isActive?: boolean;
  status?: "active" | "terminated" | "revoked" | "expired";
  terminatedAt?: string | null;
  terminationReason?: string | null;
}

export interface SessionTimelineEvent {
  id: number;
  eventType: string;
  confidenceScore: number | null;
  details: Record<string, unknown>;
  createdAt: string;
  source?: "session_event" | "audit_log";
}

/**
 * RFC-003 M1a — honest revocation status persisted at
 * `termination_context.revocation`. Mirrors the api-gateway
 * `lib/revocation-status.ts` shape.
 */
export type DownstreamStatus = "verified" | "unverified" | "no_subscriber";

export interface RevocationChannelOutcome {
  ok: boolean;
  verified?: boolean;
  at?: string;
  ackAt?: string | null;
  detail?: string;
}

export interface SessionRevocationStatus {
  receiptId?: string;
  downstreamStatus: DownstreamStatus;
  verifiedBy?: "pep_first_reject" | "ssf_ack" | null;
  channels: Record<string, RevocationChannelOutcome>;
  updatedAt: string;
}

/** `termination_context` with the typed `revocation` slice broken out. */
export type TerminationContext =
  | (Record<string, unknown> & { revocation?: SessionRevocationStatus })
  | null;

export interface SessionDetailPayload {
  sessionId: string;
  tenantExternalId: string;
  identityExternalId: string;
  status: "active" | "expired" | "revoked" | "terminated";
  isActive: boolean;
  startedAt: string | null;
  expiresAt: string | null;
  terminatedAt: string | null;
  terminationReason: string | null;
  terminationExplanation: string | null;
  terminationContext: TerminationContext;
  currentConfidenceScore: number;
  lastEvaluatedAt: string | null;
  ipAddress: string | null;
  deviceId: string | null;
  /** RFC-004 R3.3 — persisted, observed-only country (null when it was a geo default). */
  country?: string | null;
  /** RFC-004 R3.3 — OAuth/OIDC app client id persisted on the session row. */
  appClientId?: string | null;
  /** RFC-004 R3.2 — posture provenance: "synthetic" = defaults, "telemetry" = real, null = unknown. */
  postureProvenance?: "synthetic" | "telemetry" | null;
  reasoning: string | null;
  effectiveAction: string | null;
  autonomous: boolean | null;
  autonomyGateReason: string | null;
  vectorScores: Record<string, unknown> | null;
  riskStatus: string | null;
  anomalyScore: number | null;
  behavioralScore: number | null;
  /** RFC-003 C1 — JIT/PAM grants linked to this session (see cascade on revoke). */
  elevations?: ElevationGrantDto[];
  /** Count of currently `approved` grants among {@link elevations}. */
  activeElevationCount?: number;
}

export async function fetchDashboardSessionDetail(input: {
  tenantExternalId?: string;
  sessionId: string;
}): Promise<SessionDetailPayload> {
  const res = await fetch(
    `${API_BASE}/dashboard/sessions/${encodeURIComponent(input.sessionId)}/detail`,
    { headers: dashboardHeaders() },
  );
  if (!res.ok) throw new Error(`session detail failed: ${res.status}`);
  return (await res.json()) as SessionDetailPayload;
}

export interface SessionDiagnosticsPayload {
  sessionId: string;
  status: string;
  terminationReason: string | null;
  terminationExplanation: string | null;
  terminationContext: TerminationContext;
  reasoning: string | null;
  autonomyGateReason: string | null;
  effectiveAction: string | null;
  autonomous: boolean | null;
  decisionTraceId?: string | null;
  auditEvents: Array<{
    id: number;
    event: string;
    timestamp: string;
    summary?: string;
    details?: Record<string, unknown>;
  }>;
}

export async function fetchDashboardSessionDiagnostics(input: {
  tenantExternalId?: string;
  sessionId: string;
}): Promise<SessionDiagnosticsPayload> {
  const res = await fetch(
    `${API_BASE}/dashboard/sessions/${encodeURIComponent(input.sessionId)}/diagnose`,
    { headers: dashboardHeaders() },
  );
  if (!res.ok) throw new Error(`session diagnostics failed: ${res.status}`);
  return (await res.json()) as SessionDiagnosticsPayload;
}

export async function fetchDashboardSessionTimeline(input: {
  tenantExternalId: string;
  sessionId: string;
}): Promise<{ sessionId: string; events: SessionTimelineEvent[]; hint?: string }> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  q.set("sessionId", input.sessionId);
  const res = await fetch(`${API_BASE}/dashboard/session-timeline?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`session-timeline failed: ${res.status}`);
  const data = (await res.json()) as { sessionId: string; events?: SessionTimelineEvent[]; hint?: string };
  return { sessionId: data.sessionId, events: Array.isArray(data.events) ? data.events : [], hint: data.hint };
}

// ─── Predictive Session Pruning (Phase 6) ─────────────────────────────────────

export interface ScopeTransitionItem {
  id: number;
  sessionId: string;
  tokenId: string | null;
  identityExternalId: string;
  previousScopes: string[];
  newScopes: string[];
  prunedResources: string[];
  reason: string;
  driftVelocityAtTransition: number | null;
  confidenceScoreAtTransition: number | null;
  createdAt: string;
}

export interface ScopeTransitionsResponse {
  items: ScopeTransitionItem[];
  total: number;
  hint?: string;
}

export async function fetchDashboardScopeTransitions(input: {
  tenantExternalId: string;
  sessionId?: string;
  identityExternalId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<ScopeTransitionsResponse> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  if (input.sessionId) q.set("sessionId", input.sessionId);
  if (input.identityExternalId) q.set("identityExternalId", input.identityExternalId);
  if (input.from) q.set("from", input.from);
  if (input.to) q.set("to", input.to);
  if (input.limit != null) q.set("limit", String(input.limit));
  if (input.offset != null) q.set("offset", String(input.offset));
  const res = await fetch(`${API_BASE}/dashboard/scope-transitions?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`scope-transitions failed: ${res.status}`);
  const data = (await res.json()) as ScopeTransitionsResponse;
  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: typeof data.total === "number" ? data.total : 0,
    hint: data.hint,
  };
}


export async function fetchDashboardSessions(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  action?: ConfidenceAction;
  identityType?: string;
  sort?: "confidence" | "lastEvaluated" | "identity" | "terminated";
  sortDir?: "asc" | "desc";
  scope?: "active" | "history" | "all";
}): Promise<{
  items: DashboardSessionRow[];
  total: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
  stats?: { allow: number; challenge: number; revoke: number };
}> {
  const tenantExternalId = getTenantExternalId();
  const q = new URLSearchParams();
  if (tenantExternalId) q.set("tenantExternalId", tenantExternalId);
  if (params?.limit != null) q.set("limit", String(params.limit));
  if (params?.offset != null) q.set("offset", String(params.offset));
  if (params?.search?.trim()) q.set("search", params.search.trim());
  if (params?.action) q.set("action", params.action);
  if (params?.identityType && params.identityType !== "all") {
    q.set("identityType", params.identityType);
  }
  if (params?.sort) q.set("sort", params.sort);
  if (params?.sortDir) q.set("sortDir", params.sortDir);
  if (params?.scope) q.set("scope", params.scope);
  const res = await fetch(`${API_BASE}/dashboard/sessions?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) {
    throw new Error(`sessions failed: ${res.status}`);
  }
  return res.json() as Promise<{
    items: DashboardSessionRow[];
    total: number;
    limit?: number;
    offset?: number;
    hasMore?: boolean;
    stats?: { allow: number; challenge: number; revoke: number };
  }>;
}

/** JIT / ZSP elevation grant (JSON from governance API). */
export interface ElevationGrantDto {
  externalId: string;
  identityId: number;
  identityExternalId?: string | null;
  identityDisplayName?: string | null;
  identityIdentifier?: string | null;
  sessionId?: string | null;
  requestedScopes: string[];
  grantedScopes: string[];
  status: string;
  justification: string | null;
  expiresAt: string | null;
  createdAt: string;
  policyRuleMatched?: string | null;
  approvalDecision?: string | null;
  requiredApprovers?: number;
  currentApprovers?: number;
  isBreakGlass?: boolean;
  /** PAM linkage — set when this grant is a privileged-app-scoped PAM grant (null for generic JIT). */
  protectedAppExternalId?: string | null;
  /** What this grant provisions on approve: token | lease | both. */
  provisioning?: string;
  /** Ephemeral IA session id minted on approve (token/both). */
  provisionedAt?: string | null;
  /** jit-secret-provisioner lease id (lease/both). */
  leaseExternalId?: string | null;
  /** PAM request-time TTL (seconds), capped by policy maxTtlSeconds. */
  requestedTtlSeconds?: number | null;
  /** Structured reason a grant was closed (operator|expired|risk_triggered|provision_failed). */
  revokeReason?: string | null;
}

export interface ProvisionedSecretDto {
  leaseExternalId: string;
  resourceType: string;
  resourceRef: string;
  expiresAt: string;
}

export async function createElevationGrant(input: {
  identityExternalId: string;
  requestedScopes: string[];
  justification?: string;
}): Promise<{
  externalId: string;
  status: string;
  autoApproved?: boolean;
  requirement?: { decision: string; reason?: string };
  provisionedSecrets?: ProvisionedSecretDto[];
}> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) {
    throw new Error("Set a tenant external ID in Settings.");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`create elevation failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return res.json() as Promise<{
    externalId: string;
    status: string;
    autoApproved?: boolean;
    requirement?: { decision: string; reason?: string };
    provisionedSecrets?: ProvisionedSecretDto[];
  }>;
}

export async function verifyElevationReceipt(grantExternalId: string): Promise<{
  verified: boolean;
  reason?: string;
  payload?: Record<string, unknown>;
}> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) {
    throw new Error("Set a tenant external ID in Settings.");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations/${encodeURIComponent(grantExternalId)}/receipt`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`receipt verification failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return res.json() as Promise<{
    verified: boolean;
    reason?: string;
    payload?: Record<string, unknown>;
  }>;
}

export async function voteElevationGrant(input: {
  grantExternalId: string;
  approverIdentityExternalId: string;
  decision: "approve" | "deny";
  comment?: string;
  grantedScopes?: string[];
  expiresAt?: string;
}): Promise<{
  grant?: ElevationGrantDto;
  provisionedSecrets?: ProvisionedSecretDto[];
  quorumReached?: boolean;
  approveCount?: number;
  requiredApprovers?: number;
  finalDecision?: string;
}> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) throw new Error("Set a tenant external ID in Settings.");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations/${encodeURIComponent(input.grantExternalId)}/vote`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({
        approverIdentityExternalId: input.approverIdentityExternalId,
        decision: input.decision,
        comment: input.comment,
        grantedScopes: input.grantedScopes,
        expiresAt: input.expiresAt,
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`vote elevation failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return res.json() as Promise<{
    grant?: ElevationGrantDto;
    provisionedSecrets?: ProvisionedSecretDto[];
    quorumReached?: boolean;
    approveCount?: number;
    requiredApprovers?: number;
    finalDecision?: string;
  }>;
}

export async function fetchElevationApprovalStatus(grantExternalId: string): Promise<{
  grantExternalId: string;
  requiredApprovers: number;
  approveCount: number;
  denyCount: number;
  quorumReached: boolean;
  finalDecision: string;
  currentVotes?: Array<{
    approverIdentityExternalId: string;
    decision: "approve" | "deny";
    votedAt?: string;
  }>;
}> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) throw new Error("Set a tenant external ID in Settings.");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations/${encodeURIComponent(grantExternalId)}/approval-status`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`approval status failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return res.json() as Promise<{
    grantExternalId: string;
    requiredApprovers: number;
    approveCount: number;
    denyCount: number;
    quorumReached: boolean;
    finalDecision: string;
    currentVotes?: Array<{
      approverIdentityExternalId: string;
      decision: "approve" | "deny";
      votedAt?: string;
    }>;
  }>;
}

export async function fetchElevations(
  status?: string,
  opts?: {
    limit?: number;
    offset?: number;
    search?: string;
    scopeContains?: string;
    /** Filter by grant kind: "pam" (privileged-app-scoped) or "jit" (generic). */
    kind?: "pam" | "jit";
    /** Filter to a specific privileged Protected Application (PAM grants). */
    protectedAppExternalId?: string;
  }
): Promise<{
  items: ElevationGrantDto[];
  total: number;
  limit?: number;
  offset?: number;
}> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) {
    throw new Error("Set a tenant external ID in Settings.");
  }
  const q = new URLSearchParams();
  if (status) q.set("status", status);
  if (opts?.limit != null) q.set("limit", String(opts.limit));
  if (opts?.offset != null) q.set("offset", String(opts.offset));
  if (opts?.search?.trim()) q.set("search", opts.search.trim());
  if (opts?.scopeContains?.trim()) q.set("scopeContains", opts.scopeContains.trim());
  if (opts?.kind) q.set("kind", opts.kind);
  if (opts?.protectedAppExternalId) q.set("protectedAppExternalId", opts.protectedAppExternalId);
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations?${q.toString()}`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    throw new Error(`elevations failed: ${res.status}`);
  }
  return res.json() as Promise<{
    items: ElevationGrantDto[];
    total: number;
    limit?: number;
    offset?: number;
  }>;
}

export async function approveElevationGrant(input: {
  grantExternalId: string;
  grantedScopes: string[];
  expiresAtIso: string;
  approverIdentityExternalId: string;
}): Promise<{ grant?: ElevationGrantDto; provisionedSecrets?: ProvisionedSecretDto[] }> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) {
    throw new Error("Set a tenant external ID in Settings.");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations/${encodeURIComponent(input.grantExternalId)}/approve`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({
        grantedScopes: input.grantedScopes,
        expiresAt: input.expiresAtIso,
        approverIdentityExternalId: input.approverIdentityExternalId,
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`approve elevation failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return res.json() as Promise<{ grant?: ElevationGrantDto; provisionedSecrets?: ProvisionedSecretDto[] }>;
}

export async function denyElevationGrant(input: {
  grantExternalId: string;
  approverIdentityExternalId: string;
}): Promise<void> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) {
    throw new Error("Set a tenant external ID in Settings.");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations/${encodeURIComponent(input.grantExternalId)}/deny`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({
        approverIdentityExternalId: input.approverIdentityExternalId,
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`deny elevation failed: ${res.status} ${JSON.stringify(err)}`);
  }
}

// ─── JIT Lifecycle Enhancements ───────────────────────────────────────────────

export async function cancelElevationGrant(input: {
  grantExternalId: string;
  cancelledBy: string;
}): Promise<void> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) throw new Error("Set a tenant external ID in Settings.");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations/${encodeURIComponent(input.grantExternalId)}/cancel`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ cancelledBy: input.cancelledBy }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`cancel elevation failed: ${res.status} ${JSON.stringify(err)}`);
  }
}

/** RFC-004 R1.1 — per-leg outcome of an executed operator revoke cascade. */
export interface OperatorRevokeLegs {
  vault: boolean;
  postgres: boolean;
  verdikta: boolean;
  idpKill: { status: "scheduled" | "skipped"; reason?: string };
  caep: { status: "queued" | "not_sent"; reason?: string };
  webhook: boolean;
}

export interface OperatorRevokeResult {
  revokedTokens: number;
  revokedElevations?: number;
  /** Executed cascade leg plan (honest, per-leg). Absent on older gateways. */
  legs?: OperatorRevokeLegs;
  /** True when only local teardown ran (orchestrator unavailable). */
  degraded?: boolean;
}

/**
 * Operator "kill session": full teardown of a session via the tenant-scoped
 * dashboard endpoint. Returns the number of vault tokens revoked plus the
 * executed cascade leg plan.
 */
export async function revokeSessionAsOperator(input: {
  sessionId: string;
  reason?: string;
}): Promise<OperatorRevokeResult> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) throw new Error("Set a tenant external ID in Settings.");
  const q = new URLSearchParams({ tenantExternalId });
  const res = await fetch(
    `${API_BASE}/dashboard/sessions/${encodeURIComponent(input.sessionId)}/revoke?${q.toString()}`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ reason: input.reason ?? "operator_revoke" }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`revoke session failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return res.json();
}

/**
 * RFC-004 R1.2 — read-only "Verify cascade" dry-run for a single session.
 * Resolves the session's appClientId to its protected application and returns
 * the enforcement tier + prerequisite checklist WITHOUT touching any live
 * token, IdP session, or CAEP receiver.
 */
export interface SessionRevokeDryRunPrerequisite {
  id: string;
  label: string;
  detail: string;
  where: string;
  when: string;
  how: string;
  providerConsole?: Record<string, string>;
  audience: string;
  optional?: boolean;
  satisfied: boolean;
}

export interface SessionRevokeDryRunResult {
  sessionId: string;
  appClientId: string | null;
  protectedApp: { externalId: string; name: string; integrationMode: string } | null;
  tier: string;
  derivedEnforcementTier?: string | null;
  readiness: string;
  prerequisites: SessionRevokeDryRunPrerequisite[];
  wouldRun: string[];
  plan: {
    vaultRevoke: boolean;
    idpKill: boolean;
    scopedCaepReceiverId: string | null;
    globalCaepFanout: boolean;
    idpConnectionExternalId: string | null;
  } | null;
}

export async function fetchSessionRevokeDryRun(
  sessionId: string
): Promise<SessionRevokeDryRunResult> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) throw new Error("Set a tenant external ID in Settings.");
  const q = new URLSearchParams({ tenantExternalId });
  const res = await fetch(
    `${API_BASE}/dashboard/sessions/${encodeURIComponent(sessionId)}/revoke-dry-run?${q.toString()}`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`revoke dry-run failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return res.json();
}

export async function revokeElevationGrant(input: {
  grantExternalId: string;
  revokedBy: string;
  reason?: string;
}): Promise<void> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) throw new Error("Set a tenant external ID in Settings.");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations/${encodeURIComponent(input.grantExternalId)}/revoke`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ revokedBy: input.revokedBy, reason: input.reason }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`revoke elevation failed: ${res.status} ${JSON.stringify(err)}`);
  }
}

export async function extendElevationGrant(input: {
  grantExternalId: string;
  newExpiresAt: string;
  extendedBy: string;
}): Promise<void> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) throw new Error("Set a tenant external ID in Settings.");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations/${encodeURIComponent(input.grantExternalId)}/extend`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ newExpiresAt: input.newExpiresAt, extendedBy: input.extendedBy }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`extend elevation failed: ${res.status} ${JSON.stringify(err)}`);
  }
}

export interface BreakGlassResult {
  granted: boolean;
  grantExternalId: string | null;
  incidentExternalId: string | null;
  expiresAt: string | null;
  deniedScopes: string[];
  reason: string;
}

export async function triggerBreakGlass(input: {
  identityExternalId: string;
  requestedScopes: string[];
  justification: string;
  triggeredBy: string;
  ttlSeconds?: number;
}): Promise<BreakGlassResult> {
  const tenantExternalId = getTenantExternalId();
  if (!tenantExternalId) throw new Error("Set a tenant external ID in Settings.");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/elevations/break-glass`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }
  );
  return res.json() as Promise<BreakGlassResult>;
}

export interface ConfidenceHistoryPoint {
  time: string;
  score: number;
}

export async function fetchDashboardConfidenceHistory(
  hours?: number
): Promise<{
  points: ConfidenceHistoryPoint[];
  engineSessions: number;
  source: string;
  timestamp: string;
}> {
  const tenantExternalId = getTenantExternalId();
  const q = new URLSearchParams();
  if (tenantExternalId) q.set("tenantExternalId", tenantExternalId);
  if (hours && Number.isFinite(hours)) q.set("hours", String(hours));
  const res = await fetch(`${API_BASE}/dashboard/confidence-history?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) {
    throw new Error(`confidence-history failed: ${res.status}`);
  }
  const data = (await res.json()) as {
    points?: ConfidenceHistoryPoint[];
    engineSessions: number;
    source?: string;
    timestamp: string;
  };
  return {
    points: Array.isArray(data.points) ? data.points : [],
    engineSessions: data.engineSessions,
    source: data.source ?? "empty",
    timestamp: data.timestamp,
  };
}

export interface DecisionHistoryPoint {
  time: string;
  allow: number;
  challenge: number;
  revoke: number;
  downgrade: number;
}

export async function fetchDashboardDecisionHistory(
  hours?: number
): Promise<{
  points: DecisionHistoryPoint[];
  source: string;
  timestamp: string;
}> {
  const tenantExternalId = getTenantExternalId();
  const q = new URLSearchParams();
  if (tenantExternalId) q.set("tenantExternalId", tenantExternalId);
  if (hours && Number.isFinite(hours)) q.set("hours", String(hours));
  const res = await fetch(`${API_BASE}/dashboard/decision-history?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) {
    throw new Error(`decision-history failed: ${res.status}`);
  }
  const data = (await res.json()) as {
    points?: Array<Partial<DecisionHistoryPoint> & { time: string }>;
    source?: string;
    timestamp: string;
  };
  const points: DecisionHistoryPoint[] = Array.isArray(data.points)
    ? data.points.map((p) => ({
        time: p.time,
        allow: Number(p.allow ?? 0),
        challenge: Number(p.challenge ?? 0),
        revoke: Number(p.revoke ?? 0),
        downgrade: Number(p.downgrade ?? 0),
      }))
    : [];
  return {
    points,
    source: data.source ?? "empty",
    timestamp: data.timestamp,
  };
}

export interface HealthQueueStats {
  queued: number;
  /** Cumulative dropped events since process start (never resets). */
  dropped: number;
  /** Drops within the recent window (active dropping). Alert on this, not `dropped`. */
  droppedRecently?: number;
}

export interface HealthMetricsPayload {
  uptime: number;
  activeSessions: number;
  activeTokens: number;
  telemetryQueue: HealthQueueStats | null;
  auditChQueue: HealthQueueStats | null;
  cveLookup?: Record<string, unknown> | null;
  clickhouseEnabled?: boolean;
  postgresEnabled?: boolean;
  /** ARCH-03 — configured app pool max (align with PgBouncer pool_mode in prod). */
  postgresPoolMax?: number;
  auditPersistEnabled?: boolean;
  telemetryPersistEnabled?: boolean;
  redisUrlConfigured?: boolean;
  sessionContextRedisRequired?: boolean;
  verdiktaEvalCacheDisabled?: boolean;
  verdiktaMl?: Record<string, unknown>;
  cascade?: {
    healthy: boolean;
    degraded: boolean;
    components?: Record<string, { ok: boolean; detail?: string }>;
  };
  timestamp: string;
}

/** UX-14 / MON-02: operator snapshot (same JSON as `/health/metrics`). */
export async function fetchHealthMetrics(): Promise<HealthMetricsPayload> {
  const res = await fetch(`${API_BASE}/health/detail`, { headers: dashboardHeaders() });
  if (!res.ok) throw new Error(`health detail failed: ${res.status}`);
  return res.json() as Promise<HealthMetricsPayload>;
}

export interface DependencyCheckResult {
  ok: boolean;
  latencyMs?: number;
  error?: string;
  skipped?: boolean;
}

export interface WorkerStatusRow {
  id: string;
  name: string;
  enabled: boolean;
  running: boolean;
  intervalMs?: number;
  lastRunAt?: string;
  lastDurationMs?: number;
  lastError?: string;
  lastErrorAt?: string;
  consecutiveFailures: number;
  cumulative?: Record<string, number>;
}

export interface OperationsStatus {
  timestamp: string;
  dependencies: {
    redis: DependencyCheckResult;
    postgres: DependencyCheckResult;
    clickhouse: DependencyCheckResult;
    ml: DependencyCheckResult;
  };
  workers: WorkerStatusRow[];
  pipelines: {
    capture: {
      providers: Array<{
        provider: string;
        connectionExternalId: string | null;
        lastSignInReceivedAt: string | null;
        lastSessionCreatedAt: string | null;
        captureOutcomes?: Record<string, number>;
      }>;
    } | null;
    telemetryPg: {
      queued: number;
      dropped: number;
      lastFlushAt: string | null;
      lastFlushError: string | null;
    } | null;
    telemetryCh: {
      queued: number;
      dropped: number;
      droppedRecently: number;
      circuitOpen: boolean;
    } | null;
    auditCh: { queued: number; dropped: number } | null;
    streamingIngest: {
      processed: number;
      failed: number;
      dlqCount: number;
      running: boolean;
    } | null;
    backpressure: {
      state: string;
      chWriteLatencyMs: number;
      queueDepth: number;
      memoryUsageMb: number;
      timestamp: string;
    } | null;
  };
  sessions: {
    activeSessions: number;
    activeTokens: number;
    drift: { staleActiveSessions: number; staleActiveTokens: number };
    expirySweep: { tokensExpired: number; sessionsExpired: number };
  };
  dlqs: {
    redis: { totalCount: number; oldestEntry: string | null } | null;
    pgOps: {
      totalCount: number;
      byKind: Record<string, number>;
      byStatus: Record<string, number>;
    } | null;
    siem: {
      totalPending: number;
      totalRetrying: number;
      totalExhausted: number;
      totalDelivered: number;
      oldestPending: string | null;
    } | null;
  };
  otel: {
    active: boolean;
    serviceName: string;
    exporterEndpoint: string;
    sampleRate: number;
  };
}

export async function fetchOperationsStatus(tenantExternalId?: string): Promise<OperationsStatus> {
  const q = tenantExternalId?.trim()
    ? `?${new URLSearchParams({ tenantExternalId: tenantExternalId.trim() }).toString()}`
    : "";
  const res = await fetch(`${API_BASE}/operations/status${q}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`operations status failed: ${res.status}`);
  return res.json() as Promise<OperationsStatus>;
}

export interface ObservabilityStatusPayload {
  otel: {
    active: boolean;
    serviceName: string;
    exporterEndpoint: string;
    prometheusPort: number;
    sampleRate: number;
    environment: string;
  };
  logging: {
    level: string;
    format: string;
    redaction: boolean;
    otelCorrelation: boolean;
  };
  uptime: number;
  memoryUsage: Record<string, number>;
}

export async function fetchObservabilityStatus(): Promise<ObservabilityStatusPayload> {
  const res = await fetch(`${API_BASE}/observability/status`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`observability status failed: ${res.status}`);
  return res.json() as Promise<ObservabilityStatusPayload>;
}

export interface ReportsAuditRow {
  id: number;
  tenantId: number;
  sessionId: string | null;
  identityId: number | null;
  event: string;
  details: Record<string, unknown>;
  ipAddress: string | null;
  confidenceScore: number | null;
  timestamp: string;
}

/** OPS-06: persisted recurring export definitions. */
export type ReportScheduleRow = {
  id: string;
  reportKind: "audit" | "sessions";
  recurrence: "daily" | "weekly";
  hourUtc: number;
  format: "csv";
  destination: "download" | "email" | "webhook";
  recipientEmail?: string | null;
  isActive?: boolean;
  lastRunAt?: string | null;
  lastDeliveredAt?: string | null;
};

export async function fetchReportSchedules(input: {
  tenantExternalId: string;
}): Promise<{ items: ReportScheduleRow[] }> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  const res = await fetch(`${API_BASE}/reports/schedules?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`report schedules failed: ${res.status}`);
  return res.json() as Promise<{ items: ReportScheduleRow[] }>;
}

export async function createReportSchedule(input: {
  tenantExternalId: string;
  reportKind: "audit" | "sessions";
  recurrence: "daily" | "weekly";
  hourUtc?: number;
  format?: "csv";
  destination?: "download" | "email" | "webhook";
  recipientEmail?: string | null;
}): Promise<ReportScheduleRow> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  const headers = new Headers(dashboardHeaders());
  headers.set("Content-Type", "application/json");
  const res = await fetch(`${API_BASE}/reports/schedules?${q.toString()}`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      reportKind: input.reportKind,
      recurrence: input.recurrence,
      hourUtc: input.hourUtc,
      format: input.format,
      destination: input.destination,
      recipientEmail: input.recipientEmail ?? undefined,
    }),
  });
  if (!res.ok) throw new Error(`create report schedule failed: ${res.status}`);
  return res.json() as Promise<ReportScheduleRow>;
}

export async function patchReportSchedule(input: {
  tenantExternalId: string;
  scheduleId: string;
  recipientEmail?: string | null;
  destination?: "download" | "email" | "webhook";
}): Promise<ReportScheduleRow> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  q.set("scheduleId", input.scheduleId);
  const headers = new Headers(dashboardHeaders());
  headers.set("Content-Type", "application/json");
  const body: Record<string, unknown> = {};
  if (input.recipientEmail !== undefined) {
    body.recipientEmail = input.recipientEmail;
  }
  if (input.destination !== undefined) {
    body.destination = input.destination;
  }
  const res = await fetch(`${API_BASE}/reports/schedules?${q.toString()}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`patch report schedule failed: ${res.status}`);
  return res.json() as Promise<ReportScheduleRow>;
}

export type IdentityLifecycleBucket = {
  bucketStart: string;
  event:
    | "identity_created"
    | "identity_suspended"
    | "identity_revoked"
    | "identity_quarantined"
    | "identity_reenabled";
  count: number;
};

export type IdentityLifecycleReport = {
  range: { from: string; to: string; bucket: "day" | "week" };
  series: IdentityLifecycleBucket[];
  breakdowns: {
    byType: Array<{ type: string; count: number }>;
    bySource: Array<{ source: string; count: number }>;
    riskBand: Array<{ riskBand: string; count: number }>;
  };
};

export async function fetchIdentityLifecycleReport(input: {
  tenantExternalId: string;
  from?: string;
  to?: string;
  bucket?: "day" | "week";
}): Promise<IdentityLifecycleReport> {
  const q = new URLSearchParams();
  if (input.from) q.set("from", input.from);
  if (input.to) q.set("to", input.to);
  if (input.bucket) q.set("bucket", input.bucket);
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(input.tenantExternalId)}/reports/identity-lifecycle?${q.toString()}`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`identity-lifecycle report failed: ${res.status}`);
  return res.json() as Promise<IdentityLifecycleReport>;
}

/** Download the latest CSV artifact produced by the schedule tick (requires audit:export). */
export async function downloadScheduledReportArtifact(input: {
  tenantExternalId: string;
  scheduleId: string;
}): Promise<void> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  const res = await fetch(
    `${API_BASE}/reports/schedules/${encodeURIComponent(input.scheduleId)}/download?${q.toString()}`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    throw new Error(`scheduled report download failed: ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `scheduled-${input.scheduleId}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function deleteReportSchedule(input: {
  tenantExternalId: string;
  scheduleId: string;
}): Promise<void> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  q.set("scheduleId", input.scheduleId);
  const res = await fetch(`${API_BASE}/reports/schedules?${q.toString()}`, {
    method: "DELETE",
    headers: dashboardHeaders(),
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`delete report schedule failed: ${res.status}`);
  }
}

export async function fetchReportsAudit(input: {
  tenantExternalId: string;
  from?: string;
  to?: string;
  event?: string;
  limit?: number;
  offset?: number;
  format?: "json" | "csv";
}): Promise<{ items: ReportsAuditRow[]; total: number; limit: number; offset: number }> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  if (input.from) q.set("from", input.from);
  if (input.to) q.set("to", input.to);
  if (input.event) q.set("event", input.event);
  if (input.limit != null) q.set("limit", String(input.limit));
  if (input.offset != null) q.set("offset", String(input.offset));
  if (input.format) q.set("format", input.format);

  const res = await fetch(`${API_BASE}/reports/audit?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`reports audit failed: ${res.status}`);

  if (input.format === "csv") {
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-report-${input.tenantExternalId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { items: [], total: 0, limit: 0, offset: 0 };
  }

  return res.json() as Promise<{ items: ReportsAuditRow[]; total: number; limit: number; offset: number }>;
}

export interface ReportsSessionsRow {
  id: number;
  tenantId: number;
  sessionId: string;
  identityExternalId: string;
  eventType: string;
  confidenceScore: number | null;
  details: Record<string, unknown>;
  createdAt: string;
}

export async function fetchReportsSessions(input: {
  tenantExternalId: string;
  from?: string;
  to?: string;
  identityExternalId?: string;
  limit?: number;
  offset?: number;
  format?: "json" | "csv";
}): Promise<{ items: ReportsSessionsRow[]; total: number; limit: number; offset: number }> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  if (input.from) q.set("from", input.from);
  if (input.to) q.set("to", input.to);
  if (input.identityExternalId) q.set("identityExternalId", input.identityExternalId);
  if (input.limit != null) q.set("limit", String(input.limit));
  if (input.offset != null) q.set("offset", String(input.offset));
  if (input.format) q.set("format", input.format);

  const res = await fetch(`${API_BASE}/reports/sessions?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`reports sessions failed: ${res.status}`);

  if (input.format === "csv") {
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sessions-report-${input.tenantExternalId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { items: [], total: 0, limit: 0, offset: 0 };
  }

  return res.json() as Promise<{ items: ReportsSessionsRow[]; total: number; limit: number; offset: number }>;
}

/** Export sessions as CSV from dashboard session events endpoint. */
export async function exportSessionsCsv(input: {
  tenantExternalId: string;
  from?: string;
  to?: string;
}): Promise<Blob> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  q.set("format", "csv");
  if (input.from) q.set("from", input.from);
  if (input.to) q.set("to", input.to);

  const res = await fetch(`${API_BASE}/dashboard/session-events/export?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`session export failed: ${res.status}`);
  return res.blob();
}

export interface ReportsCompliancePayload {
  tenantExternalId: string;
  attestedAt: string;
  zeroStandingPrivilege: boolean;
  activeSessionCount: number;
  activeSessions: Array<{ sessionId: string; lastSeen: string; avgConfidence: number }>;
  statement: string;
}

export async function fetchReportsCompliance(input: {
  tenantExternalId: string;
}): Promise<ReportsCompliancePayload> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  const res = await fetch(`${API_BASE}/reports/compliance?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`reports compliance failed: ${res.status}`);
  return res.json() as Promise<ReportsCompliancePayload>;
}

/** Signed tamper-evident compliance export (E9). */
export async function downloadSignedComplianceExport(input: {
  tenantExternalId: string;
  format?: "json" | "text";
}): Promise<Blob> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  q.set("format", input.format ?? "json");
  const res = await fetch(`${API_BASE}/reports/signed-compliance-export?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`signed compliance export failed: ${res.status}`);
  return res.blob();
}

export async function fetchByotStatus(input: { tenantExternalId: string }): Promise<{ totalQueued: number }> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  const res = await fetch(`${API_BASE}/byot/status?${q.toString()}`, { headers: dashboardHeaders() });
  if (!res.ok) throw new Error(`byot status failed: ${res.status}`);
  return res.json() as Promise<{ totalQueued: number }>;
}

export interface IdpSyncConnectionResult {
  connectionId: string;
  provider: string;
  created: number;
  updated: number;
  suspended: number;
  deprovisioned: number;
  errors: string[];
  /** Directory import not implemented for this provider (connectivity test may still work). */
  syncNotSupported?: boolean;
  /** IdP subjects still mapped locally but missing from latest directory snapshot (Phase C.7). */
  orphans?: Array<{ idpSubject: string; identityExternalId: string }>;
}

export interface IdpSyncRunResult {
  tenantExternalId: string;
  results: IdpSyncConnectionResult[];
}

export async function runTenantIdpSync(input: { tenantExternalId: string }): Promise<IdpSyncRunResult> {
  const res = await fetch(
    `${API_BASE}/admin/idp-sync/tenants/${encodeURIComponent(input.tenantExternalId)}/run`,
    { method: "POST", headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`idp-sync run failed: ${res.status}`);
  return res.json() as Promise<IdpSyncRunResult>;
}

/** Audit rows from `idp_sync_runs` (dashboard health). */
export interface IdpSyncRunHistoryItem {
  externalId: string;
  idpConnectionId: number;
  connectionExternalId: string;
  syncType: string;
  status: string;
  createdCount: number;
  updatedCount: number;
  suspendedCount: number;
  deprovisionedCount: number;
  errorCount: number;
  errors: unknown;
  startedAt: string;
  finishedAt: string | null;
}

export async function fetchIdpSyncRuns(input: {
  tenantExternalId: string;
  connectionExternalId?: string;
  limit?: number;
}): Promise<{ tenantExternalId: string; items: IdpSyncRunHistoryItem[] }> {
  const q = new URLSearchParams();
  if (input.connectionExternalId) q.set("connectionExternalId", input.connectionExternalId);
  if (input.limit !== undefined) q.set("limit", String(input.limit));
  const qs = q.toString();
  const res = await fetch(
    `${API_BASE}/admin/idp-sync/tenants/${encodeURIComponent(input.tenantExternalId)}/sync-runs${qs ? `?${qs}` : ""}`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`idp-sync runs failed: ${res.status}`);
  return res.json() as Promise<{ tenantExternalId: string; items: IdpSyncRunHistoryItem[] }>;
}

/** Compare IdP directory to local mappings without mutating (Phase C.7). Requires admin JWT. */
export async function runDetectOrphans(input: { tenantExternalId: string }): Promise<IdpSyncRunResult> {
  const res = await fetch(
    `${API_BASE}/admin/idp-sync/tenants/${encodeURIComponent(input.tenantExternalId)}/detect-orphans`,
    { method: "POST", headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`detect-orphans failed: ${res.status}`);
  return res.json() as Promise<IdpSyncRunResult>;
}

/** OPS-01: bulk suspend/revoke orphan identity external IDs. */
export async function remediateOrphanIdentities(input: {
  tenantExternalId: string;
  externalIds: string[];
  action: "suspend" | "revoke";
}): Promise<{ applied: string[]; failed: Array<{ externalId: string; reason: string }> }> {
  const res = await fetch(
    `${API_BASE}/admin/idp-sync/tenants/${encodeURIComponent(input.tenantExternalId)}/remediate-orphans`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ externalIds: input.externalIds, action: input.action }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`remediate-orphans failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return res.json() as Promise<{
    applied: string[];
    failed: Array<{ externalId: string; reason: string }>;
  }>;
}

/** ARCH-02: async IdP directory sync job. */
export async function runTenantIdpSyncAsync(input: {
  tenantExternalId: string;
}): Promise<{ jobId: string; status: string }> {
  const res = await fetch(
    `${API_BASE}/admin/idp-sync/tenants/${encodeURIComponent(input.tenantExternalId)}/run-async`,
    { method: "POST", headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`idp-sync async failed: ${res.status}`);
  return res.json() as Promise<{ jobId: string; status: string }>;
}

export async function fetchIdpSyncJob(jobId: string): Promise<{
  id: string;
  status: string;
  error?: string;
}> {
  const res = await fetch(`${API_BASE}/admin/idp-sync/jobs/${encodeURIComponent(jobId)}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`idp-sync job failed: ${res.status}`);
  return res.json() as Promise<{ id: string; status: string; error?: string }>;
}

export async function fetchOperatorNotifications(): Promise<{
  items: Array<{ id: number; title: string; body: string; severity: string; readAt: string | null; createdAt: string }>;
  unread: number;
}> {
  const res = await fetch(`${API_BASE}/admin/auth/notifications`, { headers: dashboardHeaders() });
  if (!res.ok) throw new Error(`notifications failed: ${res.status}`);
  return res.json() as Promise<{
    items: Array<{
      id: number;
      title: string;
      body: string;
      severity: string;
      readAt: string | null;
      createdAt: string;
    }>;
    unread: number;
  }>;
}

export async function markOperatorNotificationRead(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/admin/auth/notifications/${id}/read`, {
    method: "PATCH",
    headers: dashboardHeaders(),
  });
  if (!res.ok && res.status !== 404) throw new Error(`mark read failed: ${res.status}`);
}

/** SOC 2 evidence bundle (Phase B.4). Requires admin JWT (same as other `/reports/*` routes). */
export interface Soc2EvidencePackPayload {
  generatedAt: string;
  tenantExternalId: string;
  soc2CommonCriteriaMapping: Record<string, string>;
  evidence: {
    complianceSnapshots: unknown[];
    slaBreachEvents: unknown[];
    auditLogTotalRows: number;
  };
}

export async function fetchSoc2EvidencePack(input: {
  tenantExternalId: string;
}): Promise<Soc2EvidencePackPayload> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  const res = await fetch(`${API_BASE}/reports/soc2-evidence-pack?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`soc2 evidence pack failed: ${res.status}`);
  return res.json() as Promise<Soc2EvidencePackPayload>;
}

export async function rotateTenantScimToken(input: { tenantExternalId: string }): Promise<{ tenantExternalId: string; token: string }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(input.tenantExternalId)}/integrations/scim-token/rotate`,
    { method: "POST", headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`scim token rotate failed: ${res.status}`);
  return res.json() as Promise<{ tenantExternalId: string; token: string }>;
}

export async function testScimServiceProviderConfig(input: { tenantExternalId: string; token: string }): Promise<{ ok: boolean; status: number }> {
  const res = await fetch(
    `${API_BASE}/scim/v2/tenants/${encodeURIComponent(input.tenantExternalId)}/ServiceProviderConfig`,
    { headers: { Authorization: `Bearer ${input.token}` } }
  );
  return { ok: res.ok, status: res.status };
}

export interface ScimConfigResponse {
  tenantExternalId: string;
  enabled: boolean;
  tokenSet: boolean;
  rotatedAt: string | null;
  mappings: ScimMappingConfig;
}

/** Reads SCIM provisioning config (enable flag, token state, attribute mappings). */
export async function fetchScimConfig(input: { tenantExternalId: string }): Promise<ScimConfigResponse> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(input.tenantExternalId)}/integrations/scim/config`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`scim config load failed: ${res.status}`);
  return res.json() as Promise<ScimConfigResponse>;
}

/** Updates SCIM enable flag and/or attribute mappings. */
export async function saveScimConfig(input: {
  tenantExternalId: string;
  enabled?: boolean;
  mappings?: ScimMappingConfig;
}): Promise<{ tenantExternalId: string; enabled: boolean; mappings: ScimMappingConfig }> {
  const headers = new Headers(dashboardHeaders());
  headers.set("Content-Type", "application/json");
  const body: Record<string, unknown> = {};
  if (input.enabled !== undefined) body.enabled = input.enabled;
  if (input.mappings !== undefined) body.mappings = input.mappings;
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(input.tenantExternalId)}/integrations/scim/config`,
    { method: "PUT", headers, body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`scim config save failed: ${res.status}`);
  return res.json() as Promise<{ tenantExternalId: string; enabled: boolean; mappings: ScimMappingConfig }>;
}

/** Revokes the tenant SCIM token (clears hash, disables SCIM). */
export async function revokeTenantScimToken(input: { tenantExternalId: string }): Promise<{ tenantExternalId: string; revoked: boolean }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(input.tenantExternalId)}/integrations/scim-token/revoke`,
    { method: "POST", headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`scim token revoke failed: ${res.status}`);
  return res.json() as Promise<{ tenantExternalId: string; revoked: boolean }>;
}

/** Starts a two-phase SCIM token rotation; old token stays valid during the grace window. */
export async function startScimTokenRotation(input: { tenantExternalId: string }): Promise<{
  tenantExternalId: string;
  keyId: string;
  graceExpiresAt: string;
  token: string;
}> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(input.tenantExternalId)}/integrations/scim-token/rotation/start`,
    { method: "POST", headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`scim rotation start failed: ${res.status}`);
  return res.json() as Promise<{ tenantExternalId: string; keyId: string; graceExpiresAt: string; token: string }>;
}

/** Confirms a pending two-phase SCIM token rotation, promoting the new token. */
export async function confirmScimTokenRotation(input: { tenantExternalId: string; keyId: string }): Promise<{ tenantExternalId: string; promoted: boolean }> {
  const headers = new Headers(dashboardHeaders());
  headers.set("Content-Type", "application/json");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(input.tenantExternalId)}/integrations/scim-token/rotation/confirm`,
    { method: "POST", headers, body: JSON.stringify({ keyId: input.keyId }) }
  );
  if (!res.ok) throw new Error(`scim rotation confirm failed: ${res.status}`);
  return res.json() as Promise<{ tenantExternalId: string; promoted: boolean }>;
}

export interface ScimGroupResource {
  id: string;
  displayName: string;
  members: Array<{ value: string; display?: string }>;
  meta?: Record<string, unknown>;
}

/** Lists SCIM groups for the tenant (governance-auth read). */
export async function fetchScimGroups(input: { tenantExternalId: string }): Promise<{ items: ScimGroupResource[]; total: number; unsupported?: boolean }> {
  const q = new URLSearchParams({ tenantExternalId: input.tenantExternalId, count: "100" });
  const res = await fetch(`${API_BASE}/scim/v2/Groups?${q.toString()}`, { headers: dashboardHeaders() });
  if (res.status === 404) {
    // Groups require the DB-backed group service; treat as unsupported in this deployment.
    return { items: [], total: 0, unsupported: true };
  }
  if (!res.ok) throw new Error(`scim groups load failed: ${res.status}`);
  const body = (await res.json()) as { Resources?: ScimGroupResource[]; totalResults?: number };
  return { items: body.Resources ?? [], total: body.totalResults ?? 0 };
}

/** SCIM provisioning activity: recent audit rows sourced from SCIM provisioning. */
export async function fetchScimActivity(input: { tenantExternalId: string; limit?: number }): Promise<ReportsAuditRow[]> {
  const q = new URLSearchParams();
  q.set("tenantExternalId", input.tenantExternalId);
  q.set("scimOnly", "true");
  q.set("limit", String(input.limit ?? 100));
  q.set("offset", "0");

  const res = await fetch(`${API_BASE}/reports/audit?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`reports audit failed: ${res.status}`);
  const body = (await res.json()) as { items: ReportsAuditRow[] };
  return body.items;
}

export async function rotateTenantByotToken(input: { tenantExternalId: string }): Promise<{ tenantExternalId: string; token: string }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(input.tenantExternalId)}/integrations/byot-token/rotate`,
    { method: "POST", headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`byot token rotate failed: ${res.status}`);
  return res.json() as Promise<{ tenantExternalId: string; token: string }>;
}

export async function fetchMlHealth(): Promise<{ enabled: boolean; status: string; url?: string; error?: string }> {
  const res = await fetch(`${API_BASE}/integrations/ml/health`, { headers: dashboardHeaders() });
  if (!res.ok) throw new Error(`ml health failed: ${res.status}`);
  return res.json() as Promise<{ enabled: boolean; status: string; url?: string; error?: string }>;
}

export type WorkloadAttestationMethodRow = {
  id: string;
  name: string;
  configured: boolean;
  description: string;
  credentialFields: string[];
};

/** Supported workload attestation methods (gateway env configuration). */
export async function fetchWorkloadAttestationMethods(): Promise<{
  methods: WorkloadAttestationMethodRow[];
}> {
  const res = await fetch(`${API_BASE}/workload/methods`, { headers: dashboardHeaders() });
  if (!res.ok) throw new Error(`workload methods failed: ${res.status}`);
  return res.json() as Promise<{ methods: WorkloadAttestationMethodRow[] }>;
}

export interface IdentityListItem {
  externalId: string;
  identifier: string;
  displayName: string;
  type: string;
  status: string;
  metadata?: Record<string, unknown>;
  /** Live or latest persisted confidence; omitted/null when unknown. */
  confidenceScore?: number | null;
  /** Persisted identity risk score (Sprint 5A) — null when unknown/uncomputed. */
  riskScore?: number | null;
  /** Persisted identity risk band (low/medium/high/critical) — null when unknown. */
  riskBand?: string | null;
  /** IdP subject mappings (e.g. Entra OID) when linked via directory sync or SCIM. */
  idpMappings?: Array<{ provider: string; idpSubject: string }>;
  /** Convenience: preferred IdP subject (Entra mapping when present). */
  idpSubject?: string | null;
  /** RFC-004 R3.4 — numeric manager identity id (for access-review escalation). */
  managerIdentityId?: number | null;
  /** RFC-004 R3.4 — manager's external id (resolved server-side) for linking. */
  managerExternalId?: string | null;
}

export async function fetchDashboardIdentities(params?: {
  /** Overrides persisted Settings / VITE_TENANT_EXTERNAL_ID when set. */
  tenantExternalId?: string;
  limit?: number;
  offset?: number;
  search?: string;
  type?: string;
  status?: string;
}): Promise<{
  items: IdentityListItem[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  hint?: string;
}> {
  const tenant =
    params?.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    return {
      items: [],
      total: 0,
      limit: params?.limit ?? 50,
      offset: params?.offset ?? 0,
      hasMore: false,
      hint:
        "Set the tenant external ID in Settings (or VITE_TENANT_EXTERNAL_ID at build time). The API gateway needs DATABASE_URL and a matching tenant row.",
    };
  }

  const q = new URLSearchParams();
  q.set("tenantExternalId", tenant);
  if (params?.limit != null) q.set("limit", String(params.limit));
  if (params?.offset != null) q.set("offset", String(params.offset));
  if (params?.search) q.set("search", params.search);
  if (params?.type) q.set("type", params.type);
  if (params?.status) q.set("status", params.status);
  const res = await fetch(`${API_BASE}/dashboard/identities?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) {
    throw new Error(`identities failed: ${res.status}`);
  }
  const data = (await res.json()) as {
    items: Array<{
      externalId: string;
      identifier: string;
      displayName: string;
      type: string;
      status: string;
      metadata?: Record<string, unknown>;
      confidenceScore?: number | null;
      riskScore?: number | null;
      riskBand?: string | null;
    }>;
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    hint?: string;
  };
  return {
    items: data.items.map((r) => ({
      externalId: r.externalId,
      identifier: r.identifier,
      displayName: r.displayName,
      type: r.type,
      status: r.status,
      metadata: r.metadata,
      confidenceScore: r.confidenceScore,
      riskScore: r.riskScore ?? null,
      riskBand: r.riskBand ?? null,
    })),
    total: data.total,
    limit: data.limit ?? params?.limit ?? 50,
    offset: data.offset ?? params?.offset ?? 0,
    hasMore: data.hasMore,
    hint: data.hint,
  };
}

/** Live / persisted confidence for elevation approval context. */
export async function fetchDashboardIdentityConfidence(identityExternalId: string): Promise<{
  identityExternalId: string;
  confidenceScore: number | null;
  timestamp?: string;
}> {
  const tenant = getTenantExternalId();
  if (!tenant) {
    throw new Error("Tenant external ID required for dashboard identity confidence.");
  }
  const q = new URLSearchParams();
  q.set("tenantExternalId", tenant);
  const res = await fetch(
    `${API_BASE}/dashboard/identities/${encodeURIComponent(identityExternalId)}/confidence?${q.toString()}`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    throw new Error(`identity confidence failed: ${res.status}`);
  }
  return res.json() as Promise<{
    identityExternalId: string;
    confidenceScore: number | null;
    timestamp?: string;
  }>;
}

export async function suspendIdentity(input: {
  tenantExternalId?: string;
  identityExternalId: string;
}): Promise<void> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/identities/${encodeURIComponent(input.identityExternalId)}/suspend`,
    {
      method: "POST",
      headers: dashboardHeaders(),
    }
  );
  if (!res.ok) {
    throw new Error(`suspend identity failed: ${res.status}`);
  }
}

export async function revokeIdentity(input: {
  tenantExternalId?: string;
  identityExternalId: string;
}): Promise<void> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/identities/${encodeURIComponent(input.identityExternalId)}/revoke`,
    {
      method: "POST",
      headers: dashboardHeaders(),
    }
  );
  if (!res.ok) {
    throw new Error(`revoke identity failed: ${res.status}`);
  }
}

/** UX-10: batch suspend/revoke via governance bulk-actions endpoint. */
export async function bulkGovernanceIdentityActions(input: {
  tenantExternalId?: string;
  action: "suspend" | "revoke";
  externalIds: string[];
}): Promise<{ processed: number; failed: number; errors: { id: string; message: string }[] }> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/identities/bulk-actions`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ action: input.action, externalIds: input.externalIds }),
    }
  );
  if (!res.ok) {
    throw new Error(`bulk identity actions failed: ${res.status}`);
  }
  return res.json() as Promise<{
    processed: number;
    failed: number;
    errors: { id: string; message: string }[];
  }>;
}

export interface PolicyListItem {
  externalId: string;
  name: string;
  description: string | null;
  rules: Record<string, unknown>;
  minConfidenceScore: number;
  maxTokenTTLSeconds: number;
  vectorWeights: Record<string, number>;
  isActive: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export interface IdpConnectionListItem {
  externalId: string;
  provider:
    | "okta"
    | "entra"
    | "aws_iam"
    | "aws_identity_center"
    | "gcp_iam"
    | "oidc"
    | "jumpcloud"
    | "auth0"
    | "ad";
  name: string;
  status: "active" | "disabled" | "error";
  config: Record<string, unknown>;
  lastTestedAt: string | null;
  lastTestResult: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export async function fetchTenantIdpConnections(params?: {
  tenantExternalId?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: IdpConnectionListItem[]; total: number; hasMore: boolean; hint?: string }> {
  const tenant = params?.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    return {
      items: [],
      total: 0,
      hasMore: false,
      hint:
        "Set the tenant external ID in Settings (or VITE_TENANT_EXTERNAL_ID at build time) to manage IdP connections.",
    };
  }

  const q = new URLSearchParams();
  if (params?.limit != null) q.set("limit", String(params.limit));
  if (params?.offset != null) q.set("offset", String(params.offset));
  const res = await fetch(
    `${API_BASE}/admin/idp/tenants/${encodeURIComponent(tenant)}/idp-connections?${q.toString()}`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`idp-connections failed: ${res.status}`);
  return res.json() as Promise<{ items: IdpConnectionListItem[]; total: number; hasMore: boolean }>;
}

export async function createTenantIdpConnection(input: {
  tenantExternalId?: string;
  provider: IdpConnectionListItem["provider"];
  name: string;
  config?: Record<string, unknown>;
}): Promise<IdpConnectionListItem> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");

  const res = await fetch(
    `${API_BASE}/admin/idp/tenants/${encodeURIComponent(tenant)}/idp-connections`,
    {
      method: "POST",
      headers: { ...dashboardHeaders(), "content-type": "application/json" },
      body: JSON.stringify({
        provider: input.provider,
        name: input.name,
        config: input.config ?? {},
      }),
    }
  );
  if (!res.ok) throw new Error(`create idp-connection failed: ${res.status}`);
  return res.json() as Promise<IdpConnectionListItem>;
}

export async function updateTenantIdpConnection(input: {
  tenantExternalId?: string;
  idpConnectionExternalId: string;
  patch: Partial<Pick<IdpConnectionListItem, "name" | "status" | "config">>;
}): Promise<IdpConnectionListItem> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");

  const res = await fetch(
    `${API_BASE}/admin/idp/tenants/${encodeURIComponent(tenant)}/idp-connections/${encodeURIComponent(
      input.idpConnectionExternalId
    )}`,
    {
      method: "PATCH",
      headers: { ...dashboardHeaders(), "content-type": "application/json" },
      body: JSON.stringify(input.patch),
    }
  );
  if (!res.ok) throw new Error(`update idp-connection failed: ${res.status}`);
  return res.json() as Promise<IdpConnectionListItem>;
}

export async function deleteTenantIdpConnection(input: {
  tenantExternalId?: string;
  idpConnectionExternalId: string;
}): Promise<void> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");

  const res = await fetch(
    `${API_BASE}/admin/idp/tenants/${encodeURIComponent(tenant)}/idp-connections/${encodeURIComponent(
      input.idpConnectionExternalId
    )}`,
    { method: "DELETE", headers: dashboardHeaders() }
  );
  if (res.status === 404) throw new Error("IdP connection not found");
  if (!res.ok) throw new Error(`delete idp-connection failed: ${res.status}`);
}

export async function testTenantIdpConnection(input: {
  tenantExternalId?: string;
  idpConnectionExternalId: string;
}): Promise<{
  ok: boolean;
  provider: string;
  checkedAt: string;
  details: Record<string, unknown>;
  error?: string;
  remediation?: string;
}> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");
  const res = await fetch(
    `${API_BASE}/admin/idp/tenants/${encodeURIComponent(tenant)}/idp-connections/${encodeURIComponent(
      input.idpConnectionExternalId
    )}/test`,
    { method: "POST", headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`test idp-connection failed: ${res.status}`);
  return res.json() as Promise<{
    ok: boolean;
    provider: string;
    checkedAt: string;
    details: Record<string, unknown>;
    error?: string;
    remediation?: string;
  }>;
}

export async function fetchEntraSignInWebhookStatus(input: {
  tenantExternalId?: string;
  idpConnectionExternalId: string;
}): Promise<import("./governance-api.js").SignInWebhookStatusResponse> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");
  const res = await fetch(
    `${API_BASE}/admin/idp/tenants/${encodeURIComponent(tenant)}/idp-connections/${encodeURIComponent(
      input.idpConnectionExternalId
    )}/sign-in-webhook/status`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `sign-in-webhook status failed: ${res.status}`);
  }
  return res.json() as Promise<import("./governance-api.js").SignInWebhookStatusResponse>;
}

export async function enableEntraSignInWebhook(input: {
  tenantExternalId?: string;
  idpConnectionExternalId: string;
}): Promise<import("./governance-api.js").SignInWebhookStatusResponse> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");
  const res = await fetch(
    `${API_BASE}/admin/idp/tenants/${encodeURIComponent(tenant)}/idp-connections/${encodeURIComponent(
      input.idpConnectionExternalId
    )}/sign-in-webhook/enable`,
    { method: "POST", headers: dashboardHeaders() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `enable sign-in-webhook failed: ${res.status}`);
  }
  return res.json() as Promise<import("./governance-api.js").SignInWebhookStatusResponse>;
}

export async function renewEntraSignInWebhook(input: {
  tenantExternalId?: string;
  idpConnectionExternalId: string;
}): Promise<import("./governance-api.js").SignInWebhookStatusResponse> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");
  const res = await fetch(
    `${API_BASE}/admin/idp/tenants/${encodeURIComponent(tenant)}/idp-connections/${encodeURIComponent(
      input.idpConnectionExternalId
    )}/sign-in-webhook/renew`,
    { method: "POST", headers: dashboardHeaders() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `renew sign-in-webhook failed: ${res.status}`);
  }
  return res.json() as Promise<import("./governance-api.js").SignInWebhookStatusResponse>;
}

export async function fetchTenantPolicies(params?: {
  tenantExternalId?: string;
  limit?: number;
  offset?: number;
  activeOnly?: boolean;
}): Promise<{ items: PolicyListItem[]; total: number; hasMore: boolean; hint?: string }> {
  const tenant = params?.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    return {
      items: [],
      total: 0,
      hasMore: false,
      hint:
        "Set the tenant external ID in Settings (or VITE_TENANT_EXTERNAL_ID at build time) to manage policies.",
    };
  }

  const q = new URLSearchParams();
  if (params?.limit != null) q.set("limit", String(params.limit));
  if (params?.offset != null) q.set("offset", String(params.offset));
  if (params?.activeOnly) q.set("activeOnly", "true");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/policies?${q.toString()}`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    throw new Error(`policies failed: ${res.status}`);
  }
  return res.json() as Promise<{ items: PolicyListItem[]; total: number; hasMore: boolean }>;
}

export type PolicySimulateEngineResult = {
  confidenceScore: number;
  action: string;
  vectorScores: Array<{ vector: string; score: number; weight: number; signals: string[] }>;
  reasoning: string;
  downgradedScopes: string[] | null;
};

/** Runs vector scores through the real Verdikta engine (tenant + active policy weights). */
export async function simulatePolicyWithVerdiktaEngine(input: {
  tenantExternalId?: string;
  device_posture: number;
  behavioral_analytics: number;
  geo_risk: number;
  session_integrity: number;
}): Promise<PolicySimulateEngineResult> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("Set tenant external ID in Settings.");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/policies/simulate`,
    {
      method: "POST",
      headers: governanceHeadersJson(),
      body: JSON.stringify({
        device_posture: input.device_posture,
        behavioral_analytics: input.behavioral_analytics,
        geo_risk: input.geo_risk,
        session_integrity: input.session_integrity,
      }),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `policy simulate failed: ${res.status}`);
  }
  return res.json() as Promise<PolicySimulateEngineResult>;
}

export async function createTenantPolicy(
  input: {
    tenantExternalId?: string;
    name: string;
    description?: string | null;
    minConfidenceScore?: number;
    maxTokenTTLSeconds?: number;
    vectorWeights?: Record<string, number>;
    priority?: number;
    isActive?: boolean;
    rules?: Record<string, unknown>;
  }
): Promise<PolicyListItem> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(`${API_BASE}/tenants/${encodeURIComponent(tenant)}/policies`, {
    method: "POST",
    headers: governanceHeadersJson(),
    body: JSON.stringify({
      name: input.name,
      description: input.description,
      rules: input.rules ?? {},
      minConfidenceScore: input.minConfidenceScore,
      maxTokenTTLSeconds: input.maxTokenTTLSeconds,
      vectorWeights: input.vectorWeights,
      priority: input.priority,
      isActive: input.isActive,
    }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.clone().json();
      if (body && typeof body === "object") {
        const parts = [body.error, body.message].filter(
          (s): s is string => typeof s === "string" && s.length > 0
        );
        if (parts.length > 0) detail = ` — ${parts.join(": ")}`;
      }
    } catch {
      try {
        const text = (await res.text()).trim();
        if (text) detail = ` — ${text.slice(0, 300)}`;
      } catch {
        /* ignore */
      }
    }
    throw new Error(`create policy failed: ${res.status}${detail}`);
  }
  return (await res.json()) as PolicyListItem;
}

export async function updateTenantPolicy(
  input: {
    tenantExternalId?: string;
    policyExternalId: string;
    patch: {
      name?: string;
      description?: string | null;
      rules?: Record<string, unknown>;
      minConfidenceScore?: number;
      maxTokenTTLSeconds?: number;
      vectorWeights?: Record<string, number>;
      priority?: number;
      isActive?: boolean;
      /** B2: break-glass bypass for the activation simulation gate. */
      force?: boolean;
      maxRevokeSpikeFraction?: number;
    };
  }
): Promise<PolicyListItem> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/policies/${encodeURIComponent(
      input.policyExternalId
    )}`,
    {
      method: "PATCH",
      headers: governanceHeadersJson(),
      body: JSON.stringify(input.patch),
    }
  );
  if (!res.ok) {
    let detail = "";
    let gate: unknown = null;
    try {
      const body = await res.clone().json();
      if (body && typeof body === "object") {
        const parts = [body.error, body.message].filter(
          (s): s is string => typeof s === "string" && s.length > 0
        );
        if (parts.length > 0) detail = ` — ${parts.join(": ")}`;
        if (body.gate) gate = body.gate;
      }
    } catch {
      /* ignore */
    }
    const err = new Error(`update policy failed: ${res.status}${detail}`);
    (err as Error & { gate?: unknown }).gate = gate;
    throw err;
  }
  return (await res.json()) as PolicyListItem;
}

export async function deleteTenantPolicy(input: {
  tenantExternalId?: string;
  policyExternalId: string;
}): Promise<void> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/policies/${encodeURIComponent(
      input.policyExternalId
    )}`,
    {
      method: "DELETE",
      headers: governanceHeadersJson(),
    }
  );
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.clone().json();
      if (body && typeof body === "object") {
        const parts = [body.error, body.message].filter(
          (s): s is string => typeof s === "string" && s.length > 0
        );
        if (parts.length > 0) detail = ` — ${parts.join(": ")}`;
      }
    } catch {
      /* ignore */
    }
    throw new Error(`delete policy failed: ${res.status}${detail}`);
  }
}

// ── B2: policy versioning + activation simulation gate ──────────────────────
export interface PolicyVersionRecord {
  id: number;
  tenantId: number;
  policyExternalId: string;
  version: number;
  snapshot: Record<string, unknown>;
  changeSummary: string | null;
  changedBy: string | null;
  createdAt: string;
}

export interface PolicyDiffField {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface PolicyGateResult {
  passed: boolean;
  spikeFraction: number;
  maxRevokeSpikeFraction: number;
  newlyRevoked: number;
  total: number;
}

export interface PolicyCandidateSimulation {
  total: number;
  changed: number;
  unchanged: number;
  changedFraction: number;
  transitions: Record<string, number>;
  newlyRevoked: number;
  newlyAllowed: number;
  sample: Array<{
    sessionId: string;
    identityId: string;
    from: string;
    to: string;
    fromScore: number;
    toScore: number;
  }>;
  sampledFrom: number;
  source: string;
  gate: PolicyGateResult;
}

export async function fetchPolicyVersions(
  policyExternalId: string,
  tenantExternalId?: string
): Promise<PolicyVersionRecord[]> {
  const tenant = tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external ID is not set");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/policies/${encodeURIComponent(
      policyExternalId
    )}/versions`,
    { headers: governanceHeadersJson() }
  );
  if (!res.ok) throw new Error(`fetch policy versions failed: ${res.status}`);
  const body = (await res.json()) as { items: PolicyVersionRecord[] };
  return body.items;
}

export async function diffPolicyVersions(
  policyExternalId: string,
  fromVersion: number,
  toVersion: number,
  tenantExternalId?: string
): Promise<{ fields: PolicyDiffField[] }> {
  const tenant = tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external ID is not set");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/policies/${encodeURIComponent(
      policyExternalId
    )}/versions/diff?from=${fromVersion}&to=${toVersion}`,
    { headers: governanceHeadersJson() }
  );
  if (!res.ok) throw new Error(`diff policy versions failed: ${res.status}`);
  return res.json();
}

export async function rollbackPolicyVersion(
  policyExternalId: string,
  targetVersion: number,
  tenantExternalId?: string
): Promise<PolicyListItem> {
  const tenant = tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external ID is not set");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/policies/${encodeURIComponent(
      policyExternalId
    )}/versions/${targetVersion}/rollback`,
    { method: "POST", headers: governanceHeadersJson() }
  );
  if (!res.ok) throw new Error(`rollback policy failed: ${res.status}`);
  return res.json();
}

export async function simulatePolicyCandidate(
  policyExternalId: string,
  opts: {
    minConfidenceScore?: number;
    vectorWeights?: Record<string, number>;
    windowMs?: number;
    maxSamples?: number;
    source?: "telemetry" | "decision_traces";
    maxRevokeSpikeFraction?: number;
    tenantExternalId?: string;
  } = {}
): Promise<PolicyCandidateSimulation> {
  const tenant = opts.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external ID is not set");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/policies/${encodeURIComponent(
      policyExternalId
    )}/simulate`,
    {
      method: "POST",
      headers: governanceHeadersJson(),
      body: JSON.stringify({
        minConfidenceScore: opts.minConfidenceScore,
        vectorWeights: opts.vectorWeights,
        windowMs: opts.windowMs,
        maxSamples: opts.maxSamples,
        source: opts.source,
        maxRevokeSpikeFraction: opts.maxRevokeSpikeFraction,
      }),
    }
  );
  if (!res.ok) throw new Error(`simulate policy candidate failed: ${res.status}`);
  return res.json();
}

export interface WebhookSubscriptionDto {
  externalId: string;
  targetUrl: string;
  eventTypes: string[];
  isActive: boolean;
  createdAt: string;
}

export interface CreateWebhookResult {
  externalId: string;
  signingSecret: string;
}

export async function fetchWebhookSubscriptions(
  tenantExternalId?: string
): Promise<WebhookSubscriptionDto[]> {
  const tenant = tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/webhook-subscriptions`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    throw new Error(`webhook subscriptions fetch failed: ${res.status}`);
  }
  const data = (await res.json()) as
    | { items?: WebhookSubscriptionDto[] }
    | WebhookSubscriptionDto[]
    | unknown;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: WebhookSubscriptionDto[] }).items;
  }
  return [];
}

export async function createWebhookSubscription(input: {
  tenantExternalId?: string;
  targetUrl: string;
  eventTypes: string[];
}): Promise<CreateWebhookResult> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/webhook-subscriptions`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...dashboardHeaders(),
      },
      body: JSON.stringify({
        targetUrl: input.targetUrl,
        eventTypes: input.eventTypes,
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      `create webhook subscription failed: ${res.status} ${JSON.stringify(err)}`
    );
  }
  return (await res.json()) as CreateWebhookResult;
}

export async function deleteWebhookSubscription(input: {
  tenantExternalId?: string;
  externalId: string;
}): Promise<void> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/webhook-subscriptions/${encodeURIComponent(
      input.externalId
    )}`,
    {
      method: "DELETE",
      headers: dashboardHeaders(),
    }
  );
  if (!res.ok) {
    throw new Error(`delete webhook subscription failed: ${res.status}`);
  }
}

export type WebhookDeliveryRow = {
  id: number;
  eventType: string;
  targetUrl: string;
  httpStatus: number | null;
  ok: boolean;
  errorMessage: string | null;
  createdAt: string;
};

export async function fetchWebhookDeliveries(
  tenantExternalId?: string,
  limit = 50,
  opts?: { status?: "failed" }
): Promise<{ items: WebhookDeliveryRow[] }> {
  const tenant = tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const q = new URLSearchParams({ limit: String(limit) });
  if (opts?.status === "failed") q.set("status", "failed");
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/webhook-deliveries?${q}`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    throw new Error(`webhook deliveries fetch failed: ${res.status}`);
  }
  return res.json() as Promise<{ items: WebhookDeliveryRow[] }>;
}

export async function rotateWebhookSubscriptionSecret(input: {
  tenantExternalId?: string;
  externalId: string;
}): Promise<CreateWebhookResult> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/webhook-subscriptions/${encodeURIComponent(
      input.externalId
    )}/rotate-secret`,
    {
      method: "POST",
      headers: governanceHeadersJson(),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`rotate webhook secret failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return (await res.json()) as CreateWebhookResult;
}

export async function retryWebhookDelivery(input: {
  tenantExternalId?: string;
  deliveryId: number;
}): Promise<{ ok: boolean; httpStatus?: number; errorMessage?: string | null }> {
  const tenant = input.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/webhook-deliveries/${input.deliveryId}/retry`,
    {
      method: "POST",
      headers: governanceHeadersJson(),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`webhook retry failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return (await res.json()) as {
    ok: boolean;
    httpStatus?: number;
    errorMessage?: string | null;
  };
}

export type AwsAuditEventRow = {
  id: number;
  eventTime: string;
  eventSource: string;
  eventName: string;
  awsAccountId: string | null;
  awsRegion: string | null;
  requestId: string | null;
  principalArn: string | null;
  principalType: string | null;
  sessionIssuerArn: string | null;
  roleSessionName: string | null;
  identityExternalId?: string | null;
  elevationGrantExternalId?: string | null;
  sourceIp: string | null;
  userAgent: string | null;
  createdAt: string;
};

export async function fetchAwsAuditEvents(params?: {
  tenantExternalId?: string;
  limit?: number;
  offset?: number;
  eventName?: string;
  principalContains?: string;
  from?: string;
  to?: string;
}): Promise<{ items: AwsAuditEventRow[]; total: number; hasMore: boolean; hint?: string }> {
  const tenant = params?.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    return { items: [], total: 0, hasMore: false, hint: "Set tenant external id in Settings." };
  }
  const q = new URLSearchParams();
  q.set("tenantExternalId", tenant);
  q.set("limit", String(params?.limit ?? 50));
  q.set("offset", String(params?.offset ?? 0));
  if (params?.eventName) q.set("eventName", params.eventName);
  if (params?.principalContains) q.set("principalContains", params.principalContains);
  if (params?.from) q.set("from", params.from);
  if (params?.to) q.set("to", params.to);
  const res = await fetch(`${API_BASE}/dashboard/aws/audit-events?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) {
    throw new Error(`aws audit events fetch failed: ${res.status}`);
  }
  return res.json() as Promise<{ items: AwsAuditEventRow[]; total: number; hasMore: boolean }>;
}

export type AwsPostureFindingRow = {
  id: number;
  findingTime: string;
  source: string;
  findingType: string;
  severity: string | null;
  resourceArn: string | null;
  resourceType: string | null;
  awsAccountId: string | null;
  awsRegion: string | null;
  createdAt: string;
};

export async function fetchAwsPostureFindings(params?: {
  tenantExternalId?: string;
  limit?: number;
  offset?: number;
  source?: string;
  findingType?: string;
  from?: string;
  to?: string;
}): Promise<{ items: AwsPostureFindingRow[]; total: number; hasMore: boolean; hint?: string }> {
  const tenant = params?.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    return { items: [], total: 0, hasMore: false, hint: "Set tenant external id in Settings." };
  }
  const q = new URLSearchParams();
  q.set("tenantExternalId", tenant);
  q.set("limit", String(params?.limit ?? 50));
  q.set("offset", String(params?.offset ?? 0));
  if (params?.source) q.set("source", params.source);
  if (params?.findingType) q.set("findingType", params.findingType);
  if (params?.from) q.set("from", params.from);
  if (params?.to) q.set("to", params.to);
  const res = await fetch(`${API_BASE}/dashboard/aws/posture-findings?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) {
    throw new Error(`aws posture findings fetch failed: ${res.status}`);
  }
  return res.json() as Promise<{ items: AwsPostureFindingRow[]; total: number; hasMore: boolean }>;
}

/**
 * Cloud Identity Security — unified cloud posture findings (AWS + GCP + Azure).
 */
export type CloudPostureFindingRow = {
  id: number;
  tenantId: number;
  cloud: string;
  providerFindingId: string;
  severity: string;
  category: string;
  title: string;
  description: string | null;
  resource: string | null;
  metadata: Record<string, unknown>;
  detectedAt: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export async function fetchCloudPostureFindings(params?: {
  tenantExternalId?: string;
  cloud?: string;
  severity?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: CloudPostureFindingRow[]; total: number; hasMore: boolean }> {
  const tenant = params?.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    return { items: [], total: 0, hasMore: false };
  }
  const q = new URLSearchParams();
  q.set("tenantExternalId", tenant);
  q.set("limit", String(params?.limit ?? 50));
  q.set("offset", String(params?.offset ?? 0));
  if (params?.cloud && params.cloud !== "all") q.set("cloud", params.cloud);
  if (params?.severity) q.set("severity", params.severity);
  if (params?.status) q.set("status", params.status);
  const res = await fetch(`${API_BASE}/dashboard/cloud/posture-findings?${q.toString()}`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) {
    throw new Error(`cloud posture findings fetch failed: ${res.status}`);
  }
  return res.json() as Promise<{ items: CloudPostureFindingRow[]; total: number; hasMore: boolean }>;
}

/**
 * Cloud Identity Security — Cloud IAM Policy Drift events + snapshots.
 */
export type CloudPolicyDriftEvent = {
  id: number;
  event: string;
  sessionId: string | null;
  details: Record<string, unknown>;
  timestamp: string;
};

export type CloudPolicySnapshot = {
  id: number;
  tenantId: number;
  cloud: string;
  resourceType: string;
  resourceId: string;
  policyDocument: Record<string, unknown>;
  snapshotHash: string;
  capturedAt: string;
};

export async function fetchCloudPolicyDrift(params?: {
  tenantExternalId?: string;
  limit?: number;
}): Promise<{ events: CloudPolicyDriftEvent[]; total: number }> {
  const tenant = params?.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) return { events: [], total: 0 };
  const q = new URLSearchParams();
  q.set("limit", String(params?.limit ?? 50));
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/cloud-policy-drift?${q.toString()}`,
    { headers: governanceHeadersJson() }
  );
  if (!res.ok) throw new Error(`cloud policy drift fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchCloudPolicySnapshots(params?: {
  tenantExternalId?: string;
  cloud?: string;
  limit?: number;
}): Promise<{ snapshots: CloudPolicySnapshot[]; total: number }> {
  const tenant = params?.tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) return { snapshots: [], total: 0 };
  const q = new URLSearchParams();
  q.set("limit", String(params?.limit ?? 50));
  if (params?.cloud && params.cloud !== "all") q.set("cloud", params.cloud);
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/cloud-policy-drift/snapshots?${q.toString()}`,
    { headers: governanceHeadersJson() }
  );
  if (!res.ok) throw new Error(`cloud policy snapshots fetch failed: ${res.status}`);
  return res.json();
}

export type RateLimitBucketSnapshot = {
  key: string;
  count: number;
  resetAt: number;
};

export type ApiRateLimitTierConfigDto = {
  windowMs: number;
  maxRequests: number;
};

/** Keys aligned with gateway `RATE_LIMITS` / governance policy. */
export type RateLimitPolicyTierKey =
  | "PUBLIC"
  | "ADMIN_AUTH_LOGIN"
  | "DASHBOARD"
  | "SMB"
  | "ENTERPRISE"
  | "PREMIUM"
  | "TELEMETRY"
  | "SCIM_INBOUND"
  | "IDP_LOG_STREAM";

export type ApiRateLimitOverridesDto = Partial<
  Record<RateLimitPolicyTierKey, Partial<ApiRateLimitTierConfigDto>>
>;

export type PlatformRateLimitsResponse = {
  code: Record<string, ApiRateLimitTierConfigDto>;
  overrides: ApiRateLimitOverridesDto;
  effective: Record<string, ApiRateLimitTierConfigDto>;
};

export type TenantRateLimitSummaryResponse = {
  code: Record<string, ApiRateLimitTierConfigDto>;
  platform: ApiRateLimitOverridesDto;
  platformEffective: Record<string, ApiRateLimitTierConfigDto>;
  tenant: ApiRateLimitOverridesDto;
  effective: Record<string, ApiRateLimitTierConfigDto>;
};

export async function fetchRateLimitUsage(tenantExternalId?: string): Promise<{
  tenantExternalId: string;
  mode: string;
  buckets: RateLimitBucketSnapshot[];
  note?: string;
  effectiveTiers?: {
    SMB: ApiRateLimitTierConfigDto;
    PREMIUM: ApiRateLimitTierConfigDto;
    SCIM_INBOUND: ApiRateLimitTierConfigDto;
    TELEMETRY: ApiRateLimitTierConfigDto;
    IDP_LOG_STREAM: ApiRateLimitTierConfigDto;
  };
}> {
  const tenant = tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/rate-limit-usage`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) {
    throw new Error(`rate limit usage fetch failed: ${res.status}`);
  }
  return res.json() as Promise<{
    tenantExternalId: string;
    mode: string;
    buckets: RateLimitBucketSnapshot[];
    note?: string;
    effectiveTiers?: {
      SMB: ApiRateLimitTierConfigDto;
      PREMIUM: ApiRateLimitTierConfigDto;
      SCIM_INBOUND: ApiRateLimitTierConfigDto;
      TELEMETRY: ApiRateLimitTierConfigDto;
      IDP_LOG_STREAM: ApiRateLimitTierConfigDto;
    };
  }>;
}

export async function fetchPlatformRateLimits(): Promise<PlatformRateLimitsResponse> {
  const res = await fetch(`${API_BASE}/platform/rate-limits`, { headers: governanceHeadersJson() });
  if (!res.ok) {
    throw new Error(`platform rate limits fetch failed: ${res.status}`);
  }
  return res.json() as Promise<PlatformRateLimitsResponse>;
}

export async function patchPlatformRateLimits(
  patch: ApiRateLimitOverridesDto
): Promise<{ overrides: ApiRateLimitOverridesDto; effective: Record<string, ApiRateLimitTierConfigDto> }> {
  const res = await fetch(`${API_BASE}/platform/rate-limits`, {
    method: "PATCH",
    headers: governanceHeadersJson(),
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`platform rate limits patch failed: ${res.status} ${t}`);
  }
  return res.json() as Promise<{
    overrides: ApiRateLimitOverridesDto;
    effective: Record<string, ApiRateLimitTierConfigDto>;
  }>;
}

export async function fetchTenantRateLimits(): Promise<TenantRateLimitSummaryResponse> {
  const tenant = getTenantExternalId()?.trim();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/rate-limits`,
    { headers: governanceHeadersJson() }
  );
  if (!res.ok) {
    throw new Error(`tenant rate limits fetch failed: ${res.status}`);
  }
  return res.json() as Promise<TenantRateLimitSummaryResponse>;
}

export async function patchTenantRateLimits(
  patch: ApiRateLimitOverridesDto
): Promise<TenantRateLimitSummaryResponse> {
  const tenant = getTenantExternalId()?.trim();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(`${API_BASE}/tenants/${encodeURIComponent(tenant)}/rate-limits`, {
    method: "PATCH",
    headers: governanceHeadersJson(),
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`tenant rate limits patch failed: ${res.status} ${t}`);
  }
  return res.json() as Promise<TenantRateLimitSummaryResponse>;
}

/** P0-UX-03 — platform fleet defaults (platform admin). */
export type FleetDefaultsPayload = {
  verdiktaAllowThreshold: number;
  verdiktaDowngradeThreshold: number;
  verdiktaChallengeThreshold: number;
  verdiktaRevokeThreshold: number;
  defaultSessionTtlSeconds: number;
  defaultEphemeralTokenTtlSeconds: number;
  elevationMaxDurationSeconds: number;
};

export async function fetchPlatformFleetDefaults(): Promise<{ defaults: FleetDefaultsPayload }> {
  const res = await fetch(`${API_BASE}/platform/defaults`, { headers: governanceHeadersJson() });
  if (!res.ok) {
    throw new Error(`platform defaults fetch failed: ${res.status}`);
  }
  return res.json() as Promise<{ defaults: FleetDefaultsPayload }>;
}

export async function patchPlatformFleetDefaults(
  patch: Partial<FleetDefaultsPayload>
): Promise<{ defaults: FleetDefaultsPayload }> {
  const res = await fetch(`${API_BASE}/platform/defaults`, {
    method: "PATCH",
    headers: governanceHeadersJson(),
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`platform defaults patch failed: ${res.status} ${t}`);
  }
  return res.json() as Promise<{ defaults: FleetDefaultsPayload }>;
}

/** Platform background-worker config (enable flags + intervals); env fallback when unset. */
export type PlatformWorkerConfigPayload = {
  nhiDiscoveryEnabled?: boolean;
  nhiDiscoveryIntervalMinutes?: number;
  nhiAutoRemediationEnabled?: boolean;
  nhiAutoRemediationIntervalMinutes?: number;
  ciemSyncEnabled?: boolean;
  agentDiscoveryEnabled?: boolean;
  agentContinuousEvalEnabled?: boolean;
  trustSlaEscalationEnabled?: boolean;
  armourGraphCacheEnabled?: boolean;
  auditEmbeddingEnabled?: boolean;
  retentionSweepEnabled?: boolean;
};

export async function fetchPlatformWorkerConfig(): Promise<{
  workerConfig: PlatformWorkerConfigPayload;
}> {
  const res = await fetch(`${API_BASE}/platform/worker-config`, {
    headers: governanceHeadersJson(),
  });
  if (!res.ok) throw new Error(`worker config fetch failed: ${res.status}`);
  return res.json() as Promise<{ workerConfig: PlatformWorkerConfigPayload }>;
}

export async function patchPlatformWorkerConfig(
  patch: PlatformWorkerConfigPayload
): Promise<{ workerConfig: PlatformWorkerConfigPayload }> {
  const res = await fetch(`${API_BASE}/platform/worker-config`, {
    method: "PATCH",
    headers: governanceHeadersJson(),
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`worker config patch failed: ${res.status} ${t}`);
  }
  return res.json() as Promise<{ workerConfig: PlatformWorkerConfigPayload }>;
}

/** P2-UX-12 — resolved fleet TTL / thresholds for current tenant (tenant governance JWT). */
export type TenantFleetOverrideFields = {
  verdiktaAllowThreshold: number | null;
  verdiktaDowngradeThreshold: number | null;
  verdiktaChallengeThreshold: number | null;
  verdiktaRevokeThreshold: number | null;
  defaultSessionTtlSeconds: number | null;
  defaultEphemeralTokenTtlSeconds: number | null;
  elevationMaxDurationSeconds: number | null;
};

export type TenantFleetSummaryResponse = {
  platform: FleetDefaultsPayload;
  effective: FleetDefaultsPayload;
  override: TenantFleetOverrideFields | null;
};

export async function fetchTenantFleetSummary(): Promise<TenantFleetSummaryResponse> {
  const tenant = getTenantExternalId()?.trim();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/fleet-defaults`,
    { headers: governanceHeadersJson() }
  );
  if (!res.ok) {
    throw new Error(`tenant fleet defaults fetch failed: ${res.status}`);
  }
  return res.json() as Promise<TenantFleetSummaryResponse>;
}

export async function patchTenantFleetDefaults(
  patch: Partial<FleetDefaultsPayload>
): Promise<{ platform: FleetDefaultsPayload; effective: FleetDefaultsPayload }> {
  const tenant = getTenantExternalId()?.trim();
  if (!tenant) {
    throw new Error("tenant external ID is not set");
  }
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/fleet-defaults`,
    {
      method: "PATCH",
      headers: governanceHeadersJson(),
      body: JSON.stringify(patch),
    }
  );
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`tenant fleet defaults patch failed: ${res.status} ${t}`);
  }
  return res.json() as Promise<{ platform: FleetDefaultsPayload; effective: FleetDefaultsPayload }>;
}

// ─── Platform operations console ─────────────────────────────────────────────

export type PlatformOperationsStatus = {
  opsMode: string | null;
  destructiveOpsEnabled: boolean;
  migrations: {
    expected: number;
    applied: number;
    pending: number;
    upToDate: boolean;
  };
  database: { connected: boolean };
  redis: { configured: boolean; connected: boolean | null };
  workers: Record<string, boolean>;
  retention: {
    defaultDays: number;
    revocationReceiptsDays?: number;
    decisionTracesDays?: number;
    trustSlaSamplesDays?: number;
    cutoffs: Record<string, string>;
  };
};

export async function fetchPlatformOperationsStatus(): Promise<PlatformOperationsStatus> {
  const res = await fetch(`${API_BASE}/admin/operations/status`, {
    headers: governanceHeadersJson(),
  });
  if (!res.ok) throw new Error(`operations status failed: ${res.status}`);
  return res.json() as Promise<PlatformOperationsStatus>;
}

async function postOps(path: string, body?: unknown): Promise<unknown> {
  const res = await fetch(`${API_BASE}/admin/operations${path}`, {
    method: "POST",
    headers: governanceHeadersJson(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`operations ${path} failed: ${res.status} ${t}`);
  }
  return res.json();
}

export async function resetPlatformFleetDefaultsOps(): Promise<{ message: string }> {
  return postOps("/fleet-defaults/reset") as Promise<{ message: string }>;
}

export async function resetPlatformRateLimitsOps(): Promise<{ message: string }> {
  return postOps("/rate-limits/reset") as Promise<{ message: string }>;
}

export async function runRetentionSweepOps(dryRun: boolean): Promise<unknown> {
  return postOps(`/retention/sweep?dryRun=${dryRun ? "true" : "false"}`);
}

export async function runDemoSeedOps(reset = false): Promise<unknown> {
  return postOps(`/demo-seed/run?reset=${reset ? "true" : "false"}`);
}

export async function resetDemoSeedOps(): Promise<{ removedTenants: number; message: string }> {
  return postOps("/demo-seed/reset") as Promise<{ removedTenants: number; message: string }>;
}

export async function resetSandboxTenantOps(
  tenantExternalId: string,
  confirm: string
): Promise<{ deletedCounts: Record<string, number>; message: string }> {
  return postOps(`/tenants/${encodeURIComponent(tenantExternalId)}/sandbox-reset`, {
    confirm,
  }) as Promise<{ deletedCounts: Record<string, number>; message: string }>;
}

export type DlqStats = {
  totalEntries?: number;
  bySource?: Record<string, number>;
};

export async function fetchDlqStats(): Promise<DlqStats> {
  const res = await fetch(`${API_BASE}/admin/dlq/stats`, { headers: governanceHeadersJson() });
  if (!res.ok) throw new Error(`dlq stats failed: ${res.status}`);
  return res.json() as Promise<DlqStats>;
}

export async function purgeDlqEntries(before: string, limit = 100): Promise<{ removed: number }> {
  const q = new URLSearchParams({ before, limit: String(limit) });
  const res = await fetch(`${API_BASE}/admin/dlq/entries?${q.toString()}`, {
    method: "DELETE",
    headers: governanceHeadersJson(),
  });
  if (!res.ok) throw new Error(`dlq purge failed: ${res.status}`);
  return res.json() as Promise<{ removed: number }>;
}

export async function replayDlqEntries(ids: string[]): Promise<unknown> {
  const res = await fetch(`${API_BASE}/admin/dlq/replay`, {
    method: "POST",
    headers: governanceHeadersJson(),
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`dlq replay failed: ${res.status}`);
  return res.json();
}

/**
 * RFC-004 R3.2 — list DLQ entries (optionally filtered by source), so the
 * Sessions page can offer a "Replay dropped sign-ins" action for `mapping_miss`
 * entries instead of only linking to the capture config page.
 */
export interface DlqEntry {
  id: string;
  source?: string;
  error?: string;
  payload?: unknown;
  createdAt?: string;
}

export async function fetchDlqEntries(opts?: {
  source?: string;
  errorPattern?: string;
  limit?: number;
}): Promise<DlqEntry[]> {
  const q = new URLSearchParams();
  if (opts?.source) q.set("source", opts.source);
  if (opts?.errorPattern) q.set("errorPattern", opts.errorPattern);
  if (opts?.limit) q.set("limit", String(opts.limit));
  const res = await fetch(`${API_BASE}/admin/dlq/entries?${q.toString()}`, {
    headers: governanceHeadersJson(),
  });
  if (!res.ok) throw new Error(`dlq list failed: ${res.status}`);
  const body = (await res.json()) as { entries: DlqEntry[] };
  return body.entries ?? [];
}

// ───────────────────────────────────────────────────────────────────────────
// RFC-001 organic-onboarding endpoints (Sprints 1-4 UI wiring).
// ───────────────────────────────────────────────────────────────────────────

function tenantOrThrow(tenantExternalId?: string): string {
  const tenant = tenantExternalId?.trim() || getTenantExternalId();
  if (!tenant) throw new Error("tenant external id not set");
  return tenant;
}

// --- JIT approval policy (S1.1) ---------------------------------------------

export interface JitApprovalRequirement {
  decision: "auto_approve" | "single_approval" | "multi_approval" | "deny";
  requiredApprovers: number;
  maxTtlSeconds: number;
  breakGlassEligible: boolean;
}
export interface JitPolicyRuleConfig {
  id: string;
  name: string;
  priority: number;
  conditions: Record<string, unknown>;
  requirement: JitApprovalRequirement;
}
export interface JitPolicyConfig {
  rules: JitPolicyRuleConfig[];
  defaultRequirement: JitApprovalRequirement;
}
export interface JitConfigResponse {
  tenantExternalId: string;
  source: "tenant" | "default";
  config: JitPolicyConfig;
}

export async function fetchJitConfig(tenantExternalId?: string): Promise<JitConfigResponse> {
  const tenant = tenantOrThrow(tenantExternalId);
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/integrations/jit/config`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`jit config load failed: ${res.status}`);
  return res.json() as Promise<JitConfigResponse>;
}

export async function saveJitConfig(
  config: JitPolicyConfig,
  tenantExternalId?: string
): Promise<JitConfigResponse> {
  const tenant = tenantOrThrow(tenantExternalId);
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/integrations/jit/config`,
    { method: "PUT", headers: governanceHeadersJson(), body: JSON.stringify(config) }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`jit config save failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return res.json() as Promise<JitConfigResponse>;
}

export async function resetJitConfig(tenantExternalId?: string): Promise<JitConfigResponse> {
  const tenant = tenantOrThrow(tenantExternalId);
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/integrations/jit/reset`,
    { method: "POST", headers: governanceHeadersJson() }
  );
  if (!res.ok) throw new Error(`jit config reset failed: ${res.status}`);
  return res.json() as Promise<JitConfigResponse>;
}

// --- Tenant config facade (S2.1) + observing/SCIM status (S2/S4.2) ----------

export interface TenantConfigResponse {
  tenantExternalId: string;
  idpSync: { enabled: boolean; intervalMs: number };
  scim: { enabled: boolean; tokenSet: boolean };
  jit: { source: "tenant" | "default"; config: JitPolicyConfig };
  autoProvision:
    | {
        version?: number;
        mode?: string;
        provisionedAt?: string;
        fromProvider?: string;
        sections?: string[];
        scimAvailable?: boolean;
      }
    | null;
}

export async function fetchTenantConfig(tenantExternalId?: string): Promise<TenantConfigResponse> {
  const tenant = tenantOrThrow(tenantExternalId);
  const res = await fetch(`${API_BASE}/tenants/${encodeURIComponent(tenant)}/config`, {
    headers: dashboardHeaders(),
  });
  if (!res.ok) throw new Error(`tenant config load failed: ${res.status}`);
  return res.json() as Promise<TenantConfigResponse>;
}

export async function saveTenantConfig(
  patch: { idpSync?: { enabled: boolean; intervalMs: number }; jitPolicy?: JitPolicyConfig },
  tenantExternalId?: string
): Promise<unknown> {
  const tenant = tenantOrThrow(tenantExternalId);
  const res = await fetch(`${API_BASE}/tenants/${encodeURIComponent(tenant)}/config`, {
    method: "PUT",
    headers: governanceHeadersJson(),
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`tenant config save failed: ${res.status} ${JSON.stringify(err)}`);
  }
  return res.json();
}

// --- Unified policy coverage (RFC-004 R4.5a) ---------------------------------

export type PolicyStoreSource = "tenant" | "default";

export interface UnifiedSessionPolicySummary {
  externalId: string;
  name: string;
  minConfidenceScore: number;
  maxTokenTTLSeconds: number;
  isActive: boolean;
  priority: number;
  version: number;
  hasVersions: boolean;
  updatedAt: string | null;
}

export interface UnifiedAgentPolicySummary {
  challengeThreshold?: number;
  revokeThreshold?: number;
  defaultAllowedTools?: string[];
  defaultRequireApprovalTools?: string[];
  authorizeEnforcement?: "advisory" | "enforce";
  requireDelegationChain?: boolean;
}

export interface UnifiedPoliciesResponse {
  tenantExternalId: string;
  sessionPolicies: UnifiedSessionPolicySummary[];
  jit: {
    policy: JitPolicyConfig;
    source: PolicyStoreSource;
    editEndpoint: string;
  };
  agent: {
    policy: UnifiedAgentPolicySummary;
    source: PolicyStoreSource;
    caps: { maxDelegationDepth: number; maxScopeFanOut: number };
    capsSource: PolicyStoreSource;
    editEndpoint: string;
  };
  nhi: {
    settings: {
      discovery: { connectors: Record<string, { enabled: boolean; [k: string]: unknown }> };
      remediation: { executeEnabled: boolean };
    };
    source: PolicyStoreSource;
    editEndpoint: string;
  };
  meta: {
    versionedStores: string[];
    unversionedStores: string[];
    sessionStoreAvailable: boolean;
    note: string;
  };
}

/** RFC-004 R4.5a — one read surface over the four disjoint policy stores. */
export async function fetchUnifiedPolicies(
  tenantExternalId?: string
): Promise<UnifiedPoliciesResponse> {
  const tenant = tenantOrThrow(tenantExternalId);
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/policies/unified`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`unified policies load failed: ${res.status}`);
  return res.json() as Promise<UnifiedPoliciesResponse>;
}

// --- Session integration on-ramp (S3.1) -------------------------------------

export interface SessionOnRampResponse {
  gatewayBaseUrl: string;
  tenantExternalId: string;
  endpoints: { authRequest: string; telemetryIngest: string; resolveIdp: string | null };
  sidecar: {
    image: string;
    requiredEnv: Record<string, string>;
    kubernetesManifestYaml: string;
  };
  sdk: { install: string; nodeSnippet: string };
  curl: string;
  /** RFC-004 R2.2 — integration options ordered simplest-first. */
  recommendedOrder: string[];
  /** Simplest: one identifier, server-derived context. */
  authSession: { endpoint: string; curl: string; description: string };
  /** SDK with managed refresh/rotation. */
  sdkTokenManager: { install: string; nodeSnippet: string; description: string };
  /** In-app enforcement middleware (revocations effective in <2s). */
  expressPep: { install: string; nodeSnippet: string; description: string };
}

export async function fetchSessionIntegration(
  tenantExternalId?: string
): Promise<SessionOnRampResponse> {
  const tenant = tenantOrThrow(tenantExternalId);
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenant)}/onboarding/session-integration`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`session integration load failed: ${res.status}`);
  return res.json() as Promise<SessionOnRampResponse>;
}

// --- Connector registry + instances (S1.3 / S4.1 / S4.3) --------------------

export interface ConnectorRegistryEntry {
  kind: string;
  vendor: string;
  label: string;
  version: string;
  docsUrl?: string;
  capabilities: Record<string, boolean>;
  manifest?: unknown;
}

export interface ConnectorInstance {
  id: string;
  tenantId: string;
  kind: string;
  vendor: string;
  state: string;
  grantedScopes: string[];
  config?: Record<string, unknown>;
  lastSyncAt?: string;
  healthState?: string;
  healthMessage?: string;
  createdAt?: string;
  updatedAt?: string;
  source?: "instance" | "idp_connection";
}

export async function fetchConnectorRegistry(kind?: string): Promise<ConnectorRegistryEntry[]> {
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : "";
  const res = await fetch(`${API_BASE}/connectors/registry${q}`, { headers: dashboardHeaders() });
  if (!res.ok) throw new Error(`connector registry load failed: ${res.status}`);
  const body = (await res.json()) as { connectors: ConnectorRegistryEntry[] };
  return body.connectors;
}

export async function listConnectorInstancesApi(
  tenantExternalId?: string,
  kind?: string
): Promise<ConnectorInstance[]> {
  const tenant = tenantOrThrow(tenantExternalId);
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : "";
  const res = await fetch(
    `${API_BASE}/connectors/${encodeURIComponent(tenant)}/instances${q}`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`connector instances load failed: ${res.status}`);
  const body = (await res.json()) as { instances: ConnectorInstance[] };
  return body.instances;
}

export async function checkConnectorInstanceHealth(
  externalId: string,
  tenantExternalId?: string
): Promise<ConnectorInstance> {
  const tenant = tenantOrThrow(tenantExternalId);
  const res = await fetch(
    `${API_BASE}/connectors/${encodeURIComponent(tenant)}/instances/${encodeURIComponent(externalId)}/health`,
    { headers: dashboardHeaders() }
  );
  if (!res.ok) throw new Error(`connector health failed: ${res.status}`);
  return res.json() as Promise<ConnectorInstance>;
}

export async function dryRunConnectorInstance(
  externalId: string,
  sampleSize = 100,
  tenantExternalId?: string
): Promise<unknown> {
  const tenant = tenantOrThrow(tenantExternalId);
  const res = await fetch(
    `${API_BASE}/connectors/${encodeURIComponent(tenant)}/instances/${encodeURIComponent(externalId)}/dry-run`,
    { method: "POST", headers: governanceHeadersJson(), body: JSON.stringify({ sampleSize }) }
  );
  if (!res.ok) throw new Error(`connector dry-run failed: ${res.status}`);
  return res.json();
}

export async function promoteConnectorInstance(
  externalId: string,
  tenantExternalId?: string
): Promise<ConnectorInstance> {
  const tenant = tenantOrThrow(tenantExternalId);
  const res = await fetch(
    `${API_BASE}/connectors/${encodeURIComponent(tenant)}/instances/${encodeURIComponent(externalId)}/promote`,
    { method: "POST", headers: governanceHeadersJson() }
  );
  if (!res.ok) throw new Error(`connector promote failed: ${res.status}`);
  return res.json() as Promise<ConnectorInstance>;
}

// --- Auto-provision backfill (S3.2, platform admin) -------------------------

export interface BackfillResult {
  dryRun: boolean;
  mode: string;
  count: number;
  results: Array<Record<string, unknown>>;
}

export async function runAutoProvisionBackfill(input: {
  dryRun?: boolean;
  limit?: number;
  activeOnly?: boolean;
}): Promise<BackfillResult> {
  const res = await fetch(`${API_BASE}/admin/auto-provision-backfill`, {
    method: "POST",
    headers: governanceHeadersJson(),
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`backfill failed: ${res.status}`);
  return res.json() as Promise<BackfillResult>;
}
